import { Input, Avatar, Drawer, Button, Spin, message, Modal, Image, Popover, Tooltip, Row, Col } from "antd";
import {
  SearchOutlined,
  InboxOutlined,
  PaperClipOutlined,
  SendOutlined,
  MessageOutlined,
  FileOutlined,
  FileTextOutlined,
  LinkOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import SendIcon from "../../../src/Assets/Send Buttons.svg";
import SendImage from "../../Assets/sendimage.svg";
import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { ensureSocketConnected, joinRoom, sendMessage, socket } from "../../components/Socket/socket";
import API_MANAGER from "../../API";
import axios from "axios";
import {
  getStatusMessageLabel,
  getSystemMessageText,
  isChatMessageDeleted,
  isStatusStyleMessage,
  isSystemGeneratedMessage,
  withDeletedFlag,
} from "./messageDisplay";


const ENABLE_GROUP_CHAT = true;
const ENABLE_MESSAGE_ACTIONS = true;

const { TextArea } = Input;

const ChatScreen = ({
  selectedChat,
  getProfileImageUrl,
  scrollToMessageId = null,
  onScrollToMessageHandled = null,
  embedded = false,
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [messageInput, setMessageInput] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [mediaModalVisible, setMediaModalVisible] = useState(false);
  const [mediaMessages, setMediaMessages] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [searchMessages, setSearchMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [membersDrawerVisible, setMembersDrawerVisible] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [localMembers, setLocalMembers] = useState([]);
  const [removedMembers, setRemovedMembers] = useState([]);
  const chatBodyRef = useRef(null);
  const messageRefs = useRef({});
  const fetchingRef = useRef(false);
  const selectedChatRef = useRef(selectedChat);
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const textAreaRef = useRef(null);
  const pageLimit = 1000; // Increased to load more messages per page

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  const scrollToMessage = useCallback((messageId) => {
    if (!messageId) return;
    
    const messageElement = messageRefs.current[messageId];
    if (messageElement && chatBodyRef.current) {
      setHighlightedMessageId(messageId);
      setOpenDrawer(false);
      
      setTimeout(() => {
        messageElement.scrollIntoView({ 
          behavior: "smooth", 
          block: "center" 
        });
        
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedMessageId(null);
        }, 3000);
      }, 100);
    } else {
      setOpenDrawer(false);
      setHighlightedMessageId(messageId);
      
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedMessageId(null);
      }, 3000);
    }
  }, []);
  
  // Get userId consistently
  const userId = useMemo(() => {
    return localStorage.getItem("userId") || localStorage.getItem("userID");
  }, []);

  // Ensure socket is connected on mount
  useEffect(() => {
    if (socket && userId) {
      if (!socket.connected) {
        console.log("🔄 Socket not connected, attempting to connect...");
        ensureSocketConnected();
      } else {
        console.log("✅ Socket is connected, ID:", socket.id);
      }
    }
  }, [userId]);
  const avatars = [
    "https://i.pravatar.cc/150?img=1",
    "https://i.pravatar.cc/150?img=2",
    "https://i.pravatar.cc/150?img=3",
    "https://i.pravatar.cc/150?img=4",
  ];

  // User data for the popup
  const activeUsers = [
    { name: "Carmen Davis", avatar: "https://i.pravatar.cc/150?img=1" },
    { name: "Carla Dias", avatar: "https://i.pravatar.cc/150?img=2" },
    { name: "Martin Bator", avatar: "https://i.pravatar.cc/150?img=3" },
    { name: "Cristofer Franci", avatar: "https://i.pravatar.cc/150?img=4" },
    { name: "Zain Press", avatar: "https://i.pravatar.cc/150?img=5" },
  ];
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  };

  const getAttachmentUrl = (fileKey) => {
    if (!fileKey) return null;
    if (fileKey.startsWith('http')) return fileKey;
    
    let cleanKey = fileKey;
    if (fileKey.startsWith('DOCUMENT/')) {
      cleanKey = fileKey.replace('DOCUMENT/', '');
    }
    
    return `https://dev-documents-cinchy.s3.ap-southeast-2.amazonaws.com/DOCUMENT/${cleanKey}`;
  };

  const getMediaUrl = (fileKey) => {
    if (!fileKey) return null;
    if (fileKey.startsWith('http')) return fileKey;
    
    let cleanKey = fileKey;
    // Remove MEDIA/ prefix if present
    if (fileKey.startsWith('MEDIA/') || fileKey.startsWith('media/')) {
      cleanKey = fileKey.replace(/^(MEDIA|media)\//, '');
    }
    
    // Return URL without MEDIA in the path
    return `https://dev-documents-cinchy.s3.ap-southeast-2.amazonaws.com/${cleanKey}`;
  };

  const getFileName = (fileKey) => {
    if (!fileKey) return "Document";
    
    if (fileKey.includes('/')) {
      const parts = fileKey.split('/');
      const fileName = parts[parts.length - 1];
      if (fileName.includes('.')) {
        const nameParts = fileName.split('.');
        return nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1) + " Document";
      }
      return fileName;
    }
    
    if (fileKey.includes('.')) {
      const nameParts = fileKey.split('.');
      return nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1) + " Document";
    }
    
    return "Document";
  };

  const getFileExtension = (fileKey) => {
    if (!fileKey) return "";
    const parts = fileKey.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
  };

  const isDocumentOrMediaMessage = (msg) => {
    if (!msg || !msg.type) return false;
    const type = String(msg.type).toLowerCase();
    return type === "document" || type === "media";
  };

  const applySavedCaptionEdits = (msgs) => {
    const withFlags = (Array.isArray(msgs) ? msgs : []).map(withDeletedFlag);
    try {
      const captionEdits = JSON.parse(localStorage.getItem("captionEdits") || "{}");
      if (Object.keys(captionEdits).length === 0) return withFlags;
      return withFlags.map((msg) => {
        const msgId = msg._id || msg.id;
        if (msgId && captionEdits[msgId] && !isChatMessageDeleted(msg)) {
          return { ...msg, caption: captionEdits[msgId], isEdited: true };
        }
        return msg;
      });
    } catch {
      return withFlags;
    }
  };

  const handleAttachmentClick = (fileKey) => {
    const url = getAttachmentUrl(fileKey);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
      });
    }
  };


  const formatMemberName = (member) => {
    const raw = member?.candidate?.name || member?.name ||
      `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || "";
    return raw.split(" ").filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  };

  const lsKey = (roomId) => `removedMembers_${roomId}`;

  const saveRemovedToStorage = (roomId, members) => {
    try {
      localStorage.setItem(lsKey(roomId), JSON.stringify(members));
    } catch {}
  };

  const loadRemovedFromStorage = (roomId) => {
    try {
      return JSON.parse(localStorage.getItem(lsKey(roomId)) || "[]");
    } catch { return []; }
  };

  const clearRemovedFromStorage = (roomId, memberId) => {
    try {
      const stored = loadRemovedFromStorage(roomId);
      const updated = stored.filter((m) => String(m._id) !== String(memberId));
      localStorage.setItem(lsKey(roomId), JSON.stringify(updated));
    } catch {}
  };

  const handleRemoveMember = useCallback(async (memberId) => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    if (!roomId) return;
    setActionLoadingId(memberId);
    try {
      const removedMember = localMembers.find((m) => String(m._id) === String(memberId));
      await API_MANAGER.removeMemberFromGroupChat(roomId, memberId);
      setLocalMembers((prev) => prev.filter((m) => String(m._id) !== String(memberId)));
      if (removedMember) {
        setRemovedMembers((prev) => {
          const updated = [...prev, removedMember];
          saveRemovedToStorage(roomId, updated);
          return updated;
        });
        const formattedName = formatMemberName(removedMember);
        sendMessage({ roomId, message: `${formattedName} was removed from the group`, memberRemoved: true });
        window.dispatchEvent(new CustomEvent('memberRemovedFromChat', {
          detail: { roomId, memberId: String(memberId) }
        }));
      }
      message.success("Member successfully removed.");
    } catch (error) {
      const errMsg = error?.response?.data?.message;
      message.error(typeof errMsg === "string" ? errMsg : "Failed to remove member.");
    } finally {
      setActionLoadingId(null);
    }
  }, [selectedChat, localMembers]);

  const handleAddMember = useCallback(async (participant) => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    const participantId = String(participant?._id || participant?.userId?._id || participant?.profileId?.userId?._id || "");
    if (!roomId || !participantId) return;
    setActionLoadingId(participantId);
    try {
      await API_MANAGER.addMembersToGroupChat(roomId, { members: [participantId] });
      const rawName = participant?.name || participant?.profileId?.name || participant?.candidateName ||
        `${participant?.firstName || ""} ${participant?.lastName || ""}`.trim() || "";
      const newMember = {
        _id: participantId,
        name: rawName,
        profileImage: participant?.profileImage || participant?.profileId?.profileImage || null,
      };
      const formattedName = rawName.split(" ").filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
      setLocalMembers((prev) => [...prev, newMember]);
      setAvailableParticipants((prev) => prev.filter((p) => {
        const pid = String(p?._id || p?.userId?._id || p?.profileId?.userId?._id || "");
        return pid !== participantId;
      }));
      clearRemovedFromStorage(roomId, participantId);
      setRemovedMembers((prev) => prev.filter((m) => String(m._id) !== participantId));
      sendMessage({ roomId, message: `${formattedName} was added to the group`, memberAdded: true });
      message.success("Member successfully added.");

      window.dispatchEvent(new CustomEvent('memberAddedToChat', {
        detail: { roomId, memberId: participantId, member: newMember }
      }));
    } catch (error) {
      const errMsg = error?.response?.data?.message;
      message.error(typeof errMsg === "string" ? errMsg : "Failed to add member.");
    } finally {
      setActionLoadingId(null);
    }
  }, [selectedChat]);

  const handleReAddMember = useCallback(async (member) => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    const memberId = String(member?._id);
    if (!roomId || !memberId) return;
    setActionLoadingId(memberId);
    try {
      await API_MANAGER.addMembersToGroupChat(roomId, { members: [memberId] });
      const formattedName = formatMemberName(member);
      setLocalMembers((prev) => [...prev, member]);
      setRemovedMembers((prev) => {
        const updated = prev.filter((m) => String(m._id) !== memberId);
        saveRemovedToStorage(roomId, updated);
        return updated;
      });
      clearRemovedFromStorage(roomId, memberId);
      sendMessage({ roomId, message: `${formattedName} was added to the group`, memberAdded: true });
      message.success("Member successfully added.");

      window.dispatchEvent(new CustomEvent('memberAddedToChat', {
        detail: { roomId, memberId, member }
      }));
    } catch (error) {
      const errMsg = error?.response?.data?.message;
      message.error(typeof errMsg === "string" ? errMsg : "Failed to re-add member.");
    } finally {
      setActionLoadingId(null);
    }
  }, [selectedChat]);

  const fetchMediaMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    
    setLoadingMedia(true);
    try {
      const response = await API_MANAGER.getChatMessages(roomId, {
        page: 1,
        limit: 100,
      });

      if (response?.data?.isSuccess && response?.data?.data) {
        const fetchedMessages = Array.isArray(response.data.data) 
          ? response.data.data 
          : [];
        
        // Filter Media and Document type messages
        const mediaAndDocuments = fetchedMessages.filter(
          (msg) => isDocumentOrMediaMessage(msg) && !isChatMessageDeleted(msg)
        );
        
        setMediaMessages(mediaAndDocuments);
      }
    } catch (error) {
      console.error("❌ Error fetching media messages:", error);
      message.error("Failed to load media files.");
    } finally {
      setLoadingMedia(false);
    }
  }, []);

  const handleMediaModalOpen = useCallback(() => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    if (!roomId) {
      message.warning("Please select a chat first.");
      return;
    }
    setMediaModalVisible(true);
    fetchMediaMessages(roomId);
  }, [selectedChat?.roomId, selectedChat?.id, fetchMediaMessages]);

  const fetchSearchMessages = useCallback(async (roomId, searchText = "") => {
    if (!roomId) return;
    
    setLoadingSearch(true);
    try {
      const params = {
        page: 1,
        limit: 100,
      };
      
      if (searchText.trim()) {
        params.search = searchText.trim();
      }
      
      const response = await API_MANAGER.getChatMessages(roomId, params);
      
      if (response?.data?.isSuccess && response?.data?.data) {
        const fetchedMessages = Array.isArray(response.data.data) 
          ? response.data.data 
          : [];
        setSearchMessages(fetchedMessages.reverse().map(withDeletedFlag));
      } else {
        setSearchMessages([]);
      }
    } catch (error) {
      console.error("❌ Error fetching search messages:", error);
      message.error("Failed to search messages.");
      setSearchMessages([]);
    } finally {
      setLoadingSearch(false);
    }
  }, []);

  useEffect(() => {
    if (openDrawer) {
      const roomId = selectedChat?.roomId || selectedChat?.id;
      if (roomId) {
        fetchSearchMessages(roomId, searchQuery);
      }
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      setSearchQuery("");
      setSearchMessages([]);
    }
  }, [openDrawer, selectedChat?.roomId, selectedChat?.id]);

  useEffect(() => {
    if (openDrawer) {
      const roomId = selectedChat?.roomId || selectedChat?.id;
      if (!roomId) return;
      
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        fetchSearchMessages(roomId, searchQuery);
      }, 500);
      
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [searchQuery, openDrawer, selectedChat?.roomId, selectedChat?.id, fetchSearchMessages]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (membersDrawerVisible && selectedChat) {
      const advertId = selectedChat?.advertId;
      const campaignId = selectedChat?.campaignId;
      const roomId = selectedChat?.roomId || selectedChat?.id;
      const persisted = loadRemovedFromStorage(roomId);
      const persistedIds = new Set(persisted.map((m) => String(m._id)));

      const rawMembers = (selectedChat?.members || []).filter(
        (m) => m && !persistedIds.has(String(m._id))
      );

      const isCandidateMember = (m) =>
        Boolean(
          m?.candidate ||
            m?.candidateName ||
            m?.profileId?.candidate ||
            m?.userType === "Associate" ||
            m?.role === "candidate"
        );

      const candidateMembers = rawMembers.filter(isCandidateMember);
      const recruiterRawMembers = rawMembers.filter((m) => !isCandidateMember(m));
      const recruiterIds = new Set(
        recruiterRawMembers.map((m) => String(m._id)).filter(Boolean)
      );

      setMemberSearchText("");
      setLoadingParticipants(true);

      (async () => {
        try {
          const [campaignDetailRes, campaignUsersRes] = await Promise.allSettled([
            campaignId
              ? API_MANAGER.getCampaignDetail(campaignId)
              : Promise.resolve(null),
            advertId
              ? API_MANAGER.getCampaignUsersForGroupChat(advertId)
              : Promise.resolve(null),
          ]);

          let campaignUsers = [];
          let enrichedRecruiters = recruiterRawMembers;
          if (
            campaignDetailRes.status === "fulfilled" &&
            campaignDetailRes.value
          ) {
            const campaignData =
              campaignDetailRes.value?.data?.data ||
              campaignDetailRes.value?.data ||
              {};
            campaignUsers = Array.isArray(campaignData?.users)
              ? campaignData.users
              : [];
            if (recruiterIds.size > 0) {
              const matched = campaignUsers.filter((u) =>
                recruiterIds.has(String(u?._id))
              );
              const matchedIds = new Set(matched.map((u) => String(u._id)));
              const unmatchedRaw = recruiterRawMembers.filter(
                (m) => !matchedIds.has(String(m._id))
              );
              enrichedRecruiters = [...matched, ...unmatchedRaw];
            }
          }

          const recruitersWithoutCurrentUser = enrichedRecruiters.filter(
            (m) => String(m?._id) !== String(userId)
          );

          let currentUserEntry = enrichedRecruiters.find(
            (m) => String(m?._id) === String(userId)
          );
          if (!currentUserEntry && userId) {
            const matchedUser = campaignUsers.find(
              (u) => String(u?._id) === String(userId)
            );
            if (matchedUser) {
              currentUserEntry = { ...matchedUser };
            } else {
              const loggedInUserName =
                localStorage.getItem("userName") ||
                localStorage.getItem("name") ||
                "";
              currentUserEntry = { _id: userId, name: loggedInUserName };
            }
          }

          const merged = [
            ...(currentUserEntry ? [currentUserEntry] : []),
            ...candidateMembers,
            ...recruitersWithoutCurrentUser,
          ];
          setLocalMembers(merged);

          if (
            advertId &&
            campaignUsersRes.status === "fulfilled" &&
            campaignUsersRes.value
          ) {
            const participants =
              campaignUsersRes.value?.data?.data ||
              campaignUsersRes.value?.data ||
              [];
            const availableUsers = Array.isArray(participants) ? participants : [];
            setAvailableParticipants(availableUsers);

            const availableUserIds = new Set(
              availableUsers
                .map((p) =>
                  String(
                    p?._id ||
                      p?.userId?._id ||
                      p?.profileId?.userId?._id ||
                      ""
                  )
                )
                .filter(Boolean)
            );
            availableUserIds.add(String(userId));
            const validRemoved = persisted.filter((m) => {
              const mid = String(m._id);
              return mid !== String(userId) && availableUserIds.has(mid);
            });
            setRemovedMembers(validRemoved);
            if (validRemoved.length !== persisted.length) {
              saveRemovedToStorage(roomId, validRemoved);
            }
          } else if (advertId) {
            setAvailableParticipants([]);
            setRemovedMembers(
              persisted.filter((m) => String(m._id) !== String(userId))
            );
          } else {
            setRemovedMembers(persisted);
          }
        } finally {
          setLoadingParticipants(false);
        }
      })();
    }
  }, [
    membersDrawerVisible,
    selectedChat,
    selectedChat?.advertId,
    selectedChat?.campaignId,
    selectedChat?.members,
  ]);

  const fetchMessages = useCallback(async (roomId, page = 1, append = false) => {
    console.log("📞 fetchMessages called", { roomId, page, append, fetchingRef: fetchingRef.current });
    
    if (!roomId) {
      console.warn("⚠️ No roomId provided");
      return;
    }
    
    if (fetchingRef.current) {
      console.warn("⚠️ Already fetching messages, skipping...");
      return;
    }

    fetchingRef.current = true;
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      console.log("🌐 Calling API_MANAGER.getChatMessages with:", { roomId, page, limit: pageLimit });
      const response = await API_MANAGER.getChatMessages(roomId, {
        page,
        limit: pageLimit,
      });

      console.log("✅ API Response:", response);

      if (response?.data?.isSuccess && response?.data?.data) {
        const fetchedMessages = Array.isArray(response.data.data) 
          ? response.data.data 
          : [];
        
        console.log("📨 Fetched messages:", fetchedMessages.length, fetchedMessages);
        
        if (append) {
          // When appending (loading older messages), prepend them to the beginning
          // IMPORTANT: Don't mutate the original array, create a copy first
          const reversedMessages = [...fetchedMessages].reverse();
          
          // Determine if there are more messages available
          // If we got fewer than pageLimit messages, there are no more messages
          const moreAvailable = fetchedMessages.length >= pageLimit;
          
          console.log("📥 Appending older messages:", {
            fetchedCount: fetchedMessages.length,
            reversedCount: reversedMessages.length,
            pageLimit,
            hasMore: moreAvailable
          });
          
          // Store current scroll position and height before updating
          const scrollContainer = chatBodyRef.current;
          const previousScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;
          const previousScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
          
          // Update messages and hasMore state
          setMessages((prev) => {
            const newMessages = [...reversedMessages, ...prev];
            return applySavedCaptionEdits(newMessages);
          });
          
          // Set hasMore outside of setMessages to avoid stale closure issues
          console.log("📊 Setting hasMore to:", moreAvailable, "based on", fetchedMessages.length, "messages (pageLimit:", pageLimit, ")");
          setHasMore(moreAvailable);
          
          // Maintain scroll position after loading older messages
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (chatBodyRef.current) {
                const newScrollHeight = chatBodyRef.current.scrollHeight;
                const heightDiff = newScrollHeight - previousScrollHeight;
                // Adjust scroll position to maintain visual position
                chatBodyRef.current.scrollTop = previousScrollTop + heightDiff;
              }
            }, 50);
          });
        } else {
          // When loading initial messages, reverse and scroll to bottom
          const reversedMessages = [...fetchedMessages].reverse();
          setMessages(applySavedCaptionEdits(reversedMessages));
          const moreAvailable = fetchedMessages.length >= pageLimit;
          console.log("📊 Initial load - Setting hasMore to:", moreAvailable, "based on", fetchedMessages.length, "messages");
          setHasMore(moreAvailable);
          
          // If there are more messages, automatically load them until all are fetched
          if (moreAvailable && fetchedMessages.length > 0) {
            console.log("📥 Auto-loading remaining messages...");
            // Load all remaining messages recursively
            const loadAllMessages = async (currentRoomId, currentPageNum = 1, accumulatedMessages = []) => {
              if (fetchingRef.current) return accumulatedMessages;
              
              fetchingRef.current = true;
              try {
                const nextPage = currentPageNum + 1;
                const response = await API_MANAGER.getChatMessages(currentRoomId, {
                  page: nextPage,
                  limit: pageLimit,
                });
                
                if (response?.data?.isSuccess && response?.data?.data) {
                  const newMessages = Array.isArray(response.data.data) 
                    ? response.data.data 
                    : [];
                  
                  if (newMessages.length > 0) {
                    const allMessages = [...accumulatedMessages, ...newMessages];
                    const stillHasMore = newMessages.length >= pageLimit;
                    
                    // Update messages with all accumulated messages
                    const reversedAllMessages = [...allMessages].reverse();
                    setMessages(applySavedCaptionEdits(reversedAllMessages));
                    setHasMore(stillHasMore);
                    setCurrentPage(nextPage);
                    
                    // If there are still more, continue loading
                    if (stillHasMore) {
                      fetchingRef.current = false;
                      return await loadAllMessages(currentRoomId, nextPage, allMessages);
                    } else {
                      // All messages loaded, scroll to bottom
                      setTimeout(() => {
                        if (chatBodyRef.current) {
                          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
                        }
                      }, 100);
                      return allMessages;
                    }
                  } else {
                    // No more messages
                    setHasMore(false);
                    setTimeout(() => {
                      if (chatBodyRef.current) {
                        chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
                      }
                    }, 100);
                    return accumulatedMessages;
                  }
                }
                return accumulatedMessages;
              } catch (error) {
                console.error("❌ Error loading all messages:", error);
                setHasMore(false);
                return accumulatedMessages;
              } finally {
                fetchingRef.current = false;
              }
            };
            
            // Start loading all messages after a short delay
            setTimeout(() => {
              loadAllMessages(roomId, 1, fetchedMessages);
            }, 200);
          } else {
            // No more messages, just scroll to bottom
            setTimeout(() => {
              if (chatBodyRef.current) {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
              }
            }, 100);
          }
        }

        setCurrentPage(page);
      } else {
        console.warn("⚠️ API response structure unexpected:", response);
      }
    } catch (error) {
      console.error("❌ Error fetching messages:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [pageLimit]);

  useEffect(() => {
    if (!scrollToMessageId || loading || messages.length === 0) return;
    const tryScroll = () => {
      const el = messageRefs.current[scrollToMessageId];
      if (el) {
        scrollToMessage(scrollToMessageId);
        if (onScrollToMessageHandled) onScrollToMessageHandled();
      } else {
        setTimeout(tryScroll, 300);
      }
    };
    setTimeout(tryScroll, 400);
  }, [scrollToMessageId, loading, messages.length, scrollToMessage, onScrollToMessageHandled]);

  // Track previous roomId to prevent duplicate calls
  const previousRoomIdRef = useRef(null);

  // Check if we need to load more messages when messages change and we're at the top
  useEffect(() => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    if (!roomId || !chatBodyRef.current || loading || loadingMore || fetchingRef.current || !hasMore) {
      return;
    }

    // Wait a bit for DOM to update
    const checkScroll = setTimeout(() => {
      const container = chatBodyRef.current;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtTop = scrollTop <= 250;
      const isScrollable = scrollHeight > clientHeight;

      console.log("🔍 Checking if we need to load more messages:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        isAtTop,
        isScrollable,
        hasMore,
        messagesCount: messages.length,
        currentPage
      });

      // If we're at the top and there are more messages, load them
      // Also load if container is not scrollable but hasMore is true (means we need more content)
      if (isAtTop && hasMore && !fetchingRef.current && !loadingMore) {
        console.log("📥 Auto-loading more messages - at top with hasMore=true");
        fetchingRef.current = true;
        const nextPage = currentPage + 1;
        fetchMessages(roomId, nextPage, true).catch((error) => {
          console.error("❌ Error auto-loading messages:", error);
          fetchingRef.current = false;
        });
      }
    }, 300);

    return () => clearTimeout(checkScroll);
  }, [messages.length, hasMore, currentPage, selectedChat?.roomId, selectedChat?.id, loading, loadingMore, fetchMessages]);

  useEffect(() => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    
    console.log("🔵 ChatScreen useEffect triggered", { 
      selectedChat, 
      roomId, 
      previousRoomId: previousRoomIdRef.current,
      fetchingRef: fetchingRef.current,
      hasSelectedChat: !!selectedChat
    });
    
    if (!roomId) {
      console.log("⚠️ No roomId, clearing messages");
      setMessages([]);
      setCurrentPage(1);
      setHasMore(true);
      setReplyToMessage(null);
      setEditingMessage(null);
      fetchingRef.current = false;
      previousRoomIdRef.current = null;
      return;
    }
    
    // If roomId hasn't changed, don't fetch again
    if (roomId === previousRoomIdRef.current) {
      console.log("⚠️ RoomId unchanged, skipping fetch");
      return;
    }
    
    // Update previous roomId BEFORE making the API call
    previousRoomIdRef.current = roomId;
    
    // Reset fetching flag when roomId changes to allow fresh fetch
    // IMPORTANT: Reset BEFORE calling fetchMessages, so fetchMessages can set it to true
    fetchingRef.current = false;
    
    console.log("📥 Preparing to fetch messages for roomId:", roomId);
      setMessages([]);
    setCurrentPage(1);
    setHasMore(true);
    setReplyToMessage(null);
    setEditingMessage(null);
    
    // Room join is handled once in the socket listener effect via joinRoom().

    // Fetch messages when chat is selected (clicked)
    // fetchMessages will set fetchingRef.current = true internally
    console.log("📞 Calling fetchMessages with:", { roomId, page: 1, append: false, fetchingRef: fetchingRef.current });
    
    // Use setTimeout to ensure the ref is reset before fetchMessages checks it
    setTimeout(() => {
      fetchMessages(roomId, 1, false).catch((error) => {
        console.error("❌ Error in fetchMessages:", error);
        fetchingRef.current = false;
      }).finally(() => {
        fetchingRef.current = false;
        setTimeout(() => {
          if (textAreaRef.current) {
            try {
              textAreaRef.current.focus();
              textAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } catch (e) {
              console.error("Failed to focus textarea:", e);
            }
          }
        }, 300);
      });
    }, 0);
    
    // Cleanup: Clear scroll timeout when roomId changes
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [selectedChat?.roomId, selectedChat?.id, fetchMessages, socket]);

  const currentRoomIdRef = useRef(null);
  const messageHandlerRef = useRef(null);

  useEffect(() => {
    if (!userId) {
      console.log("⚠️ UserId not available");
      return;
    }

    if (!socket) {
      console.log("⚠️ Socket not available, will retry...");
      return;
    }

      const roomId = selectedChat?.roomId || selectedChat?.id;
    currentRoomIdRef.current = roomId;
    
    if (!roomId) {
      console.log("⚠️ No roomId available for socket listener");
      return;
    }

    console.log("🔌 Setting up socket listener for roomId:", roomId);
    joinRoom(roomId);

      const handleNewMessage = (newMessage) => {
        const currentRoomId = currentRoomIdRef.current;
        console.log("💬 New message received from socket:", newMessage);
        console.log("🔍 Current roomId:", currentRoomId);
        console.log("🔍 Message roomId:", newMessage.roomId);
        console.log("🔍 Message roomId type:", typeof newMessage.roomId);
        console.log("🔍 Current roomId type:", typeof currentRoomId);
        console.log("🔍 Current userId:", userId);
        
        if (!currentRoomId) {
          console.log("⚠️ No current roomId, ignoring message");
          return;
        }
        
        // Compare roomIds as strings to handle type mismatches
        const messageRoomId = String(
          newMessage.roomId?._id ||
            newMessage.roomId?.id ||
            newMessage.roomId ||
            newMessage.room?._id ||
            newMessage.room?.id ||
            newMessage.room ||
            ""
        ).trim();
        const currentRoomIdStr = String(currentRoomId || "").trim();
        
        console.log("🔍 Comparing roomIds:", {
          messageRoomId,
          currentRoomIdStr,
          match: messageRoomId === currentRoomIdStr
        });
        
        if (messageRoomId === currentRoomIdStr && messageRoomId !== "") {
          // Normalize message type to ensure proper casing
          const normalizeMessageType = (msg) => {
            const messageContent = msg.message || "";
            
            // If type is already set, only normalize the case - never change it
            if (msg.type) {
              const typeLower = String(msg.type).toLowerCase();
              if (typeLower === "document") return "Document";
              if (typeLower === "media") return "Media";
              if (typeLower === "text") return "Text";
              if (typeLower === "system" || typeLower === "archive" || typeLower === "archived") {
                return typeLower;
              }
              return msg.type;
            }
            
            // Only infer type if it's completely missing
            // Check for DOCUMENT/ prefix first (from upload API) - this takes priority
            if (messageContent.includes('DOCUMENT/') || messageContent.includes('document/')) {
              return "Document";
            }
            
            // Check for MEDIA/ prefix
            if (messageContent.includes('MEDIA/') || messageContent.includes('media/')) {
              return "Media";
            }
            
            // Check if message looks like a file key (contains path separators)
            if (messageContent.includes('/')) {
              // For file keys without explicit prefix, check file extension
              // But be conservative - only mark as Media if it's clearly an image
              const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
              if (imageExtensions.test(messageContent)) {
                return "Media";
              }
              // Default to Document for other file types
              return "Document";
            }
            
            // Default to Text for regular messages
            return "Text";
          };
          
          newMessage.type = normalizeMessageType(newMessage);
          
          // Ensure senderId structure is consistent
          const messageSenderId = newMessage.senderId?._id || newMessage.senderId?.id || newMessage.senderId;
          
          // If senderId is a string, convert it to object structure for consistency
          if (typeof newMessage.senderId === "string") {
            newMessage.senderId = {
              _id: newMessage.senderId,
              id: newMessage.senderId,
            };
          }
          
          // If the message is from current user but senderId structure is missing, add it
          if (String(messageSenderId) === String(userId) && !newMessage.senderId) {
            newMessage.senderId = {
              _id: userId,
              id: userId,
            };
          }
          
          console.log("✅ Processing socket message:", newMessage);
          console.log("🔍 Comparing:", {
            messageSenderId,
            userId,
            match: String(messageSenderId) === String(userId)
          });
          
          // Check if this message already exists (replace temp message if it's from current user)
          setMessages((prev) => {
            // Normalize senderId for comparison
            const normalizedMessageSenderId = String(messageSenderId || "").trim();
            const normalizedUserId = String(userId || "").trim();
            const isFromCurrentUser = normalizedMessageSenderId === normalizedUserId && normalizedMessageSenderId !== "";
            
            // Also check if there's a temp message with same content (safety check)
            const hasMatchingTempMessage = prev.some(
              (msg) => msg.isTemp && msg.message === newMessage.message
            );
            
            console.log("🔍 Message sender check:", {
              messageSenderId: normalizedMessageSenderId,
              userId: normalizedUserId,
              isFromCurrentUser,
              hasMatchingTempMessage,
              messageText: newMessage.message,
              senderIdObject: newMessage.senderId
            });
            
            // System notices (campaign removal, archive) and membership broadcasts
            // are sent by the backend over the socket and must show for everyone,
            // including the user who triggered them.
            const incomingType = String(newMessage?.type || "").toLowerCase();
            const isSystemBroadcast =
              newMessage?.memberAdded ||
              newMessage?.memberRemoved ||
              incomingType === "system" ||
              incomingType === "archive" ||
              incomingType === "archived";

            // CRITICAL: If message is from current user OR has matching temp message, NEVER add it as new message
            if ((isFromCurrentUser || hasMatchingTempMessage) && !isSystemBroadcast) {
              console.log("✅ Message is from current user or has matching temp - will only replace temp or ignore");
              
              // First, check for temp message by content (within last 30 seconds for safety)
              // This should be the primary way to match
              const thirtySecondsAgo = Date.now() - 30000;
              let tempIndex = -1;
              
              // Try to find temp message by exact content match
              // For document messages, also match by type to ensure correct replacement
              tempIndex = prev.findIndex((msg) => {
                if (msg.isTemp) {
                  // Match by message content (fileKey for documents)
                  const messageMatch = msg.message === newMessage.message;
                  
                  // For document/media messages, also check type
                  const typeMatch = isDocumentOrMediaMessage(msg) 
                    ? isDocumentOrMediaMessage(newMessage)
                    : (msg.type === newMessage.type || (!msg.type && !newMessage.type));
                  
                  if (messageMatch && typeMatch) {
                  // Check if temp message was created recently
                  const tempTime = msg._id ? parseInt(msg._id.replace('temp-', '')) : 0;
                  return tempTime > thirtySecondsAgo;
                  }
                }
                return false;
              });
              
              if (tempIndex !== -1) {
                console.log("🔄 Replacing temp message with real message at index:", tempIndex);
                const newMessages = [...prev];
                const tempMsg = prev[tempIndex];
                const finalSenderId = {
                  _id: userId,
                  id: userId
                };
                const resolvedCaption = newMessage.caption || tempMsg.caption || "";
                newMessages[tempIndex] = { 
                  ...newMessage, 
                  isTemp: false,
                  senderId: finalSenderId,
                  caption: resolvedCaption,
                  replyToMessage: newMessage.replyToMessage || tempMsg.replyToMessage || null,
                };
                // Persist caption to localStorage so it survives page refresh
                const realMsgId = newMessage._id || newMessage.id;
                if (resolvedCaption && realMsgId) {
                  try {
                    const savedCaptions = JSON.parse(localStorage.getItem("captionEdits") || "{}");
                    savedCaptions[realMsgId] = resolvedCaption;
                    localStorage.setItem("captionEdits", JSON.stringify(savedCaptions));
                  } catch (e) { /* ignore */ }
                }
                return newMessages;
              }
              
              // Check if message already exists by _id or id (real message from server)
              const existingRealIndex = prev.findIndex(
                (msg) => !msg.isTemp && (msg._id === newMessage._id || msg.id === newMessage.id)
              );
              
              if (existingRealIndex !== -1) {
                console.log("⚠️ Real message from current user already exists, skipping");
                return prev;
              }
              
              // If it's from current user but no temp message found, check if there's a temp message with same fileKey
              // This can happen if the message format is slightly different
              const tempByFileKey = prev.findIndex((msg) => {
                if (msg.isTemp && 
                    isDocumentOrMediaMessage(newMessage) &&
                    isDocumentOrMediaMessage(msg)) {
                  // Try to match by fileKey - check if the message contains the same fileKey
                  const msgKey = msg.message || "";
                  const newMsgKey = newMessage.message || "";
                  // Check if keys match (handle different formats)
                  if (msgKey === newMsgKey || 
                      msgKey.includes(newMsgKey) || 
                      newMsgKey.includes(msgKey)) {
                    const tempTime = msg._id ? parseInt(msg._id.replace('temp-', '')) : 0;
                    return tempTime > thirtySecondsAgo;
                  }
                }
                return false;
              });
              
              if (tempByFileKey !== -1) {
                console.log("🔄 Found temp message by fileKey match at index:", tempByFileKey);
                const newMessages = [...prev];
                const tempMsg = prev[tempByFileKey];
                const finalSenderId = {
                  _id: userId,
                  id: userId
                };
                const resolvedCaption2 = newMessage.caption || tempMsg.caption || "";
                newMessages[tempByFileKey] = { 
                  ...newMessage, 
                  isTemp: false,
                  senderId: finalSenderId,
                  caption: resolvedCaption2,
                  replyToMessage: newMessage.replyToMessage || tempMsg.replyToMessage || null,
                };
                // Persist caption to localStorage so it survives page refresh
                const realMsgId2 = newMessage._id || newMessage.id;
                if (resolvedCaption2 && realMsgId2) {
                  try {
                    const savedCaptions = JSON.parse(localStorage.getItem("captionEdits") || "{}");
                    savedCaptions[realMsgId2] = resolvedCaption2;
                    localStorage.setItem("captionEdits", JSON.stringify(savedCaptions));
                  } catch (e) { /* ignore */ }
                }
                return newMessages;
              }
              
              // If it's from current user but no temp message found, IGNORE it completely
              // This prevents duplicate messages from appearing on left side
              console.log("🚫 IGNORING message from current user - no temp message to replace");
              return prev;
            }
            
            // For messages from other users, check if it already exists by _id or id only
            const existingIndex = prev.findIndex(
              (msg) => {
                const msgId = msg._id || msg.id;
                const newMsgId = newMessage._id || newMessage.id;
                return msgId && newMsgId && msgId === newMsgId;
              }
            );
            
            if (existingIndex !== -1) {
              console.log("⚠️ Message from other user already exists by ID, skipping");
              return prev;
            }
            
            // Add as new message (only for messages from other users)
            console.log("➕ Adding new message from other user");
            return [...prev, newMessage];
          });
          
          setTimeout(() => {
            if (chatBodyRef.current) {
              chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
            }
          }, 100);
        } else {
          console.log("⚠️ Message roomId doesn't match, ignoring");
        }
      };

    // Set up message listener - use a named function so we can remove it properly
    const messageHandler = (newMessage) => {
      console.log("📨 Message handler called with:", newMessage);
      handleNewMessage(newMessage);
    };
    
    // Store handler in ref for cleanup
    const previousHandler = messageHandlerRef.current;
    messageHandlerRef.current = messageHandler;
    
    // Remove any existing listener first to avoid duplicates
    if (previousHandler) {
      console.log("🧹 Removing previous message listener");
      socket.off("new-message", previousHandler);
    }
    // Add the listener
    socket.on("new-message", messageHandler);
    console.log("✅ Socket listener registered for roomId:", roomId, "Socket connected:", socket.connected);
    
    // Set up joined-room confirmation listener
    const handleJoinedRoom = (data) => {
      console.log("✅ Joined room confirmation:", data);
    };
    socket.on("joined-room", handleJoinedRoom);

    // Re-join on reconnect is handled centrally in socket.js via desiredRoomId.

    const resolveMemberName = (data) => {
      const memberId = String(data?.memberId || data?.userId || data?.member || "");
      const members = selectedChatRef.current?.members || [];
      const match = members.find((m) => String(m?._id) === memberId);
      const rawName = match?.candidate?.name || match?.name ||
        `${match?.firstName || ""} ${match?.lastName || ""}`.trim() ||
        data?.memberName || data?.name || "";
      if (!rawName) return null;
      return rawName.split(" ").filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    };

    const handleMemberRemoved = (data) => {
      const eventRoomId = data?.roomId || data?.room;
      if (String(eventRoomId) !== String(roomId)) return;
      const removedMemberId = String(data?.memberId || data?.userId || data?.member || "");
      if (!removedMemberId) return;
      setLocalMembers((prev) => prev.filter((m) => String(m?._id) !== removedMemberId));
      window.dispatchEvent(new CustomEvent('memberRemovedFromChat', {
        detail: { roomId: String(eventRoomId), memberId: removedMemberId }
      }));
    };

    const handleMemberLeft = (data) => {
      const eventRoomId = data?.roomId || data?.room;
      if (String(eventRoomId) !== String(roomId)) return;
      const leftUserId = String(data?.userId || data?.memberId || "");
      const formattedName = resolveMemberName(data);
      if (leftUserId === String(userId)) {
        setMessages((prev) => [
          ...prev,
          { _id: `sys-${Date.now()}`, type: "system", message: "You left the group", createdAt: new Date().toISOString() },
        ]);
      } else if (formattedName) {
        setMessages((prev) => [
          ...prev,
          { _id: `sys-${Date.now()}`, type: "system", message: `${formattedName} left the group`, createdAt: new Date().toISOString() },
        ]);
      }
    };

    socket.on("member-removed", handleMemberRemoved);
    socket.on("member-left", handleMemberLeft);
    socket.on("user-left", handleMemberLeft);

    return () => {
      console.log("🧹 Cleaning up socket listeners for roomId:", roomId);
      if (messageHandlerRef.current) {
        socket.off("new-message", messageHandlerRef.current);
        messageHandlerRef.current = null;
      }
      socket.off("joined-room", handleJoinedRoom);
      socket.off("member-removed", handleMemberRemoved);
      socket.off("member-left", handleMemberLeft);
      socket.off("user-left", handleMemberLeft);
    };
  }, [selectedChat?.roomId, selectedChat?.id, userId, socket]);

  const handleScroll = useCallback((e) => {
    // Log every scroll event when near top to verify it's firing
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const roomId = selectedChat?.roomId || selectedChat?.id;
    
    // Early return if conditions not met
    if (!roomId) {
      return;
    }
    
    // Always log when near top to debug (reduced frequency)
    if (scrollTop <= 300) {
      console.log("📜 SCROLL EVENT FIRED:", {
        scrollTop: Math.round(scrollTop),
        scrollHeight: Math.round(scrollHeight),
        clientHeight: Math.round(clientHeight),
        hasMore,
        loadingMore,
        fetching: fetchingRef.current,
        currentPage,
        canScroll: scrollHeight > clientHeight,
        scrollPercentage: Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
      });
    }
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Debounce scroll handler to prevent excessive API calls
    scrollTimeoutRef.current = setTimeout(() => {
      // Prevent multiple simultaneous calls
      if (fetchingRef.current) {
        if (scrollTop <= 300) {
          console.log("⚠️ Skipping - already fetching");
        }
        return;
      }
      
      if (loadingMore) {
        if (scrollTop <= 300) {
          console.log("⚠️ Skipping - already loading more");
        }
        return;
      }
      
      if (!hasMore) {
        if (scrollTop <= 300) {
          console.log("⚠️ Skipping - no more messages (hasMore = false)");
        }
        return;
      }
      
      // Load older messages when scrolling near the top (within 300px)
      // This triggers when user scrolls UP to see older messages
      // Increased threshold to make it easier to trigger
      if (scrollTop <= 300) {
        console.log("📜 ✅✅✅ TRIGGERING API CALL - Loading older messages", {
          scrollTop: Math.round(scrollTop),
          currentPage,
          hasMore,
          roomId,
          nextPage: currentPage + 1,
          messagesCount: messages.length
        });
        
        // Set fetching flag immediately to prevent duplicate calls
        fetchingRef.current = true;
        const nextPage = currentPage + 1;
        
        // Call the API to fetch older messages
        console.log("🚀🚀🚀 CALLING fetchMessages API:", { roomId, page: nextPage, append: true });
        fetchMessages(roomId, nextPage, true).then(() => {
          console.log("✅✅✅ Older messages loaded successfully for page:", nextPage);
        }).catch((error) => {
          console.error("❌❌❌ Error loading older messages:", error);
          fetchingRef.current = false;
          // Don't set hasMore to false on error - let the API response determine this
          // This allows retrying if it was a temporary network error
        }).finally(() => {
          // fetchingRef is set to false in fetchMessages finally block
        });
      }
    }, 150); // Debounce for 150ms
  }, [hasMore, loadingMore, currentPage, selectedChat?.roomId, selectedChat?.id, fetchMessages, messages.length]);

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    
    const roomId = selectedChat?.roomId || selectedChat?.id;
    if (!roomId) {
      message.error("Please select a chat first.");
      return;
    }

    // Store the file as pending attachment
    setPendingAttachment(file);
  }, [selectedChat?.roomId, selectedChat?.id]);

  const handleRemoveAttachment = useCallback(() => {
    setPendingAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleFileUpload = useCallback(async (file, captionText = "", replyData = null) => {
    const roomId = selectedChat?.roomId || selectedChat?.id;
    if (!roomId) {
      message.error("Please select a chat first.");
      return;
    }

    setUploadingFile(true);

    try {
      const fileExtension = file.name?.split(".").pop() || file.type?.split("/")[1] || "pdf";
      const contentType = file.type || "application/pdf";

      const uploadData = {
        extension: fileExtension,
        type: "DOCUMENT",
        contentType: contentType,
      };

      const uploadResponse = await API_MANAGER.fileUpload(uploadData);
      const uploadUrl = uploadResponse?.data?.data?.url;
      const fileKey = uploadResponse?.data?.data?.key;

      if (!uploadUrl || !fileKey) {
        throw new Error("Failed to get upload URL");
      }

      await axios.put(uploadUrl, file, {
        headers: {
          "Content-Type": contentType,
        },
      });

      const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
      
      const tempMessage = {
        _id: `temp-${Date.now()}`,
        id: `temp-${Date.now()}`,
        roomId: roomId,
        senderId: {
          _id: currentUserId,
          id: currentUserId,
        },
        message: fileKey,
        caption: captionText || "",
        createdAt: new Date().toISOString(),
        type: "Document",
        isTemp: true,
      };

      if (replyData) {
        tempMessage.replyTo = replyData._id;
        tempMessage.replyToMessage = { ...replyData };
      }

      console.log("📎 Creating temp document message:", tempMessage);
      setMessages((prev) => [...prev, tempMessage]);

      const socketPayload = {
        roomId: roomId,
        message: fileKey,
        type: "Media",
      };
      if (captionText) socketPayload.caption = captionText;
      if (replyData) socketPayload.replyTo = replyData._id;
      sendMessage(socketPayload);

      // Safety timeout: Remove spinner after 5 seconds if message isn't replaced
      // This is a fallback in case the socket message doesn't match properly
      setTimeout(() => {
        setMessages((prev) => {
          const hasTempWithThisKey = prev.some(
            (msg) => msg.isTemp && msg.message === fileKey && msg.type === "Document"
          );
          
          if (hasTempWithThisKey) {
            console.log("⏰ Safety timeout: Removing spinner from temp document message");
            return prev.map((msg) => {
              if (msg.isTemp && msg.message === fileKey && msg.type === "Document") {
                return { ...msg, isTemp: false };
              }
              return msg;
            });
          }
          return prev;
        });
      }, 5000);

      setTimeout(() => {
        if (chatBodyRef.current) {
          chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to upload file. Please try again."
      );
    } finally {
      setUploadingFile(false);
      setPendingAttachment(null); // Clear pending attachment after upload
    }
  }, [selectedChat?.roomId, selectedChat?.id]);

  const canEditMessage = useCallback((msg) => {
    // Cannot edit deleted / archive status messages
    if (isStatusStyleMessage(msg)) return false;
    // Cannot edit unsent (temp) messages
    if (msg.isTemp) return false;
    // Must be sent by current user
    const senderIdValue = msg.senderId?._id || msg.senderId?.id || msg.senderId;
    if (String(senderIdValue) !== String(userId)) return false;
    // 15-minute window from original message timestamp
    if (msg.createdAt) {
      const sentTime = new Date(msg.createdAt).getTime();
      const now = Date.now();
      const fifteenMinutes = 15 * 60 * 1000;
      if (now - sentTime > fifteenMinutes) return false;
    }
    // Text messages are always editable
    if (!isDocumentOrMediaMessage(msg)) return true;
    // Document/media messages are editable only if they have a caption
    if (msg.caption) return true;
    return false;
  }, [userId]);

  const handleEdit = useCallback((msg) => {
    if (!canEditMessage(msg)) return;
    const isDocMedia = isDocumentOrMediaMessage(msg);
    const textToEdit = isDocMedia ? (msg.caption || "") : (msg.message || "");
    setEditingMessage({
      _id: msg._id || msg.id,
      message: textToEdit,
      isCaption: isDocMedia,
    });
    setMessageInput(textToEdit);
    setReplyToMessage(null);
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [canEditMessage]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
    setMessageInput("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingMessage) return;
    const newText = messageInput.trim();
    if (!newText || newText === editingMessage.message) {
      handleCancelEdit();
      return;
    }
    setSavingEdit(true);
    try {
      if (editingMessage.isCaption) {
        // Backend doesn't support editing doc/media captions — persist locally
        const captionEdits = JSON.parse(localStorage.getItem("captionEdits") || "{}");
        captionEdits[editingMessage._id] = newText;
        localStorage.setItem("captionEdits", JSON.stringify(captionEdits));
      } else {
        await API_MANAGER.editChatMessage(editingMessage._id, { message: newText });
      }
      setMessages((prev) =>
        prev.map((msg) => {
          const msgId = msg._id || msg.id;
          if (msgId === editingMessage._id) {
            if (editingMessage.isCaption) {
              return { ...msg, caption: newText, isEdited: true };
            }
            return { ...msg, message: newText, isEdited: true };
          }
          return msg;
        })
      );
      setEditingMessage(null);
      setMessageInput("");
    } catch (error) {
      const errMsg = error?.response?.data?.message;
      message.error(typeof errMsg === "string" ? errMsg : (errMsg?.message || "Failed to edit message."));
    } finally {
      setSavingEdit(false);
    }
  }, [editingMessage, messageInput, handleCancelEdit]);

  const sendMessageHandler = useCallback(() => {
    if (editingMessage) {
      handleSaveEdit();
      return;
    }

    const roomId = selectedChat?.roomId || selectedChat?.id;
    if (!roomId) return;

    const messageText = messageInput.trim();
    const hasAttachment = pendingAttachment !== null;
    const hasText = messageText.length > 0;

    if (!hasText && !hasAttachment) return;

    if (hasAttachment) {
      handleFileUpload(pendingAttachment, hasText ? messageText : "", replyToMessage || null);
    } else if (hasText) {
      const currentUserId = localStorage.getItem("userId") || localStorage.getItem("userID");
      
      const tempMessage = {
        _id: `temp-${Date.now()}`,
        id: `temp-${Date.now()}`,
        roomId: roomId,
        senderId: {
          _id: currentUserId,
          id: currentUserId,
        },
        message: messageText,
        createdAt: new Date().toISOString(),
        type: "Text",
        isTemp: true,
      };

      if (replyToMessage) {
        tempMessage.replyTo = replyToMessage._id;
        tempMessage.replyToMessage = { ...replyToMessage };
      }

      setMessages((prev) => [...prev, tempMessage]);
      
      const msgPayload = {
        roomId: roomId,
        message: messageText,
      };
      if (replyToMessage) {
        msgPayload.replyTo = replyToMessage._id;
      }
      sendMessage(msgPayload);
    }

    setMessageInput("");
    setPendingAttachment(null);
    setReplyToMessage(null);

    setTimeout(() => {
      if (chatBodyRef.current) {
        chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
      }
    }, 100);
  }, [selectedChat?.roomId, selectedChat?.id, messageInput, pendingAttachment, handleFileUpload, replyToMessage, editingMessage, handleSaveEdit]);

  const openDeleteModal = useCallback((messageId) => {
    if (!messageId || messageId.startsWith('temp-')) {
      return;
    }
    setMessageToDelete(messageId);
    setDeleteModalVisible(true);
  }, []);

  const handleDeleteMessage = useCallback(async () => {
    if (!messageToDelete) {
      return;
    }

    setDeletingMessageId(messageToDelete);
    try {
      await API_MANAGER.deleteChatMessage(messageToDelete);
      setMessages((prev) => prev.map((msg) => {
        const msgId = msg._id || msg.id;
        if (msgId === messageToDelete) {
          return { ...msg, isDeleted: true };
        }
        return msg;
      }));
      message.success("Message deleted.");
      setDeleteModalVisible(false);
      setMessageToDelete(null);
    } catch (error) {
      const errMsg = error?.response?.data?.message;
      message.error(typeof errMsg === 'string' ? errMsg : (errMsg?.message || "Failed to delete message."));
    } finally {
      setDeletingMessageId(null);
    }
  }, [messageToDelete]);

  const handleReply = useCallback((msg) => {
    const senderIdValue = msg.senderId?._id || msg.senderId?.id || msg.senderId;
    const isMine = String(senderIdValue) === String(userId);
    setReplyToMessage({
      _id: msg._id || msg.id,
      message: msg.message,
      type: msg.type,
      senderName: isMine ? "You" : (msg.senderId?.name || msg.senderId?.candidate?.name || msg.sender?.name || selectedChat?.name || "User"),
    });
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [userId, selectedChat?.name]);

  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  const getReplyPreviewText = useCallback((msg) => {
    if (!msg) return "";
    if (isStatusStyleMessage(msg)) {
      return getStatusMessageLabel(msg, { isOwn: false });
    }
    const type = String(msg.type || "").toLowerCase();
    if (type === "document" || type === "media") {
      return "📎 Attachment";
    }
    const text = msg.message || "";
    return text.length > 80 ? text.substring(0, 80) + "..." : text;
  }, []);

  const findOriginalMessage = useCallback((replyToId) => {
    if (!replyToId) return null;
    return messages.find((m) => (m._id || m.id) === replyToId);
  }, [messages]);


  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        border: embedded ? "none" : "1px solid #eee",
        borderRadius: embedded ? 0 : "12px",
        background: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fdfdfc",
          borderRadius: embedded ? 0 : "12px 12px 0 0",
          padding: "6px 12px",
          borderBottom: "1px solid #eee",
        }}
      >
        {/* Search Box */}
        <div
          style={{ display: "flex", alignItems: "center", flex: 1 }}
          onClick={() => setOpenDrawer(true)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 20 21"
            fill="none"
          >
            <path
              d="M19 19.25L14.65 14.9M17 9.25C17 13.6683 13.4183 17.25 9 17.25C4.58172 17.25 1 13.6683 1 9.25C1 4.83172 4.58172 1.25 9 1.25C13.4183 1.25 17 4.83172 17 9.25Z"
              stroke="#242424"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <Input
            placeholder="Search Chat History"
            bordered={false}
            style={{
              fontSize: "14px",
              boxShadow: "none",
            }}
          />
        </div>

        {/* Right Section: Archive + Avatars */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {ENABLE_GROUP_CHAT && (selectedChat?.isGroup === true || selectedChat?.type === "Group" || selectedChat?.type === "group") && (
            <Tooltip title="View Members" placement="bottom">
              <span
                style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", padding: "4px", borderRadius: "6px", transition: "background 0.2s" }}
                onClick={() => setMembersDrawerVisible(true)}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <TeamOutlined style={{ fontSize: "18px", color: "#242424" }} />
              </span>
            </Tooltip>
          )}
          <Tooltip title="Shared Media" placement="bottom">
            <span
              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
              onClick={() => {
                setMediaModalVisible(true);
                handleMediaModalOpen();
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="21"
                viewBox="0 0 22 21"
                fill="none"
              >
                <path
                  d="M19.5 6.25V14.45C19.5 16.1302 19.5 16.9702 19.173 17.612C18.8854 18.1765 18.4265 18.6354 17.862 18.923C17.2202 19.25 16.3802 19.25 14.7 19.25H7.3C5.61984 19.25 4.77976 19.25 4.13803 18.923C3.57354 18.6354 3.1146 18.1765 2.82698 17.612C2.5 16.9702 2.5 16.1302 2.5 14.45V6.25M13 12.75H7.5C6.67157 12.75 6 13.4216 6 14.25C6 15.0784 6.67157 15.75 7.5 15.75H13C14.6569 15.75 16 14.4069 16 12.75C16 11.0931 14.6569 9.75 13 9.75H9M2.6 1.25H19.4C19.9601 1.25 20.2401 1.25 20.454 1.35899C20.6422 1.45487 20.7951 1.60785 20.891 1.79601C21 2.00992 21 2.28995 21 2.85V4.65C21 5.21005 21 5.49008 20.891 5.70399C20.7951 5.89215 20.6422 6.04513 20.454 6.14101C20.2401 6.25 19.9601 6.25 19.4 6.25H2.6C2.03995 6.25 1.75992 6.25 1.54601 6.14101C1.35785 6.04513 1.20487 5.89215 1.10899 5.70399C1 5.49008 1 5.21005 1 4.65V2.85C1 2.28995 1 2.00992 1.10899 1.79601C1.20487 1.60785 1.35785 1.45487 1.54601 1.35899C1.75992 1.25 2.03995 1.25 2.6 1.25Z"
                  stroke="#242424"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </Tooltip>
          {/* <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              cursor: "pointer",
            }}
            onMouseEnter={() => setShowUserPopup(true)}
            onMouseLeave={() => setShowUserPopup(false)}
          >
            {avatars.map((src, index) => (
              <Avatar
                key={index}
                src={src}
                size={32}
                style={{
                  border: "2px solid #fff",
                  marginLeft: index === 0 ? 0 : -10,
                  boxShadow: "0 0 2px rgba(0, 0, 0, 0.15)",
                }}
              />
            ))} */}

            {/* User Popup */}
            {/* {showUserPopup && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "8px",
                  background: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  zIndex: 1000,
                  minWidth: "200px",
                  padding: "12px 0",
                }}
              >
                <div
                  style={{
                    padding: "0 16px 8px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    marginBottom: "8px",
                  }}
                >
                  <h4
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#333",
                    }}
                  >
                    Active users in this chat
                  </h4>
                </div>
                {activeUsers.map((user, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 16px",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#f5f5f5";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                    }}
                  >
                    <Avatar
                      src={user.avatar}
                      size={24}
                      style={{ marginRight: "12px" }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#333",
                        fontWeight: "500",
                      }}
                    >
                      {user.name}
                    </span>
                  </div>
                ))}
              </div>
            )} */}
          {/* </div> */}
        </div>
      </div>

      {/* Chat Body */}
      {!selectedChat ? (
        <div
          style={{
            flex: 1,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <img
            src="/messagechat.svg"
            alt="Message Chat"
            style={{
              width: "200px",
              height: "auto",
              marginBottom: "24px",
            }}
          />
          <p
            style={{
              color: "#302C48",
              textAlign: "center",
              fontFamily: "Arial",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 700,
              lineHeight: "12px",
              margin: 0,
            }}
          >
            Your Messages Will Appear Here
          </p>
        </div>
      ) : (
        <>
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.7;
                }
              }
              
              .message-box-scroll::-webkit-scrollbar {
                width: 6px;
              }
              
              .message-box-scroll::-webkit-scrollbar-track {
                background: transparent;
                border-radius: 10px;
              }
              
              .message-box-scroll::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.2);
                border-radius: 10px;
              }
              
              .message-box-scroll::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 0, 0, 0.3);
              }
              
              .chat-textarea::-webkit-scrollbar {
                width: 6px;
              }
              
              .chat-textarea::-webkit-scrollbar-track {
                background: transparent;
                border-radius: 10px;
              }
              
              .chat-textarea::-webkit-scrollbar-thumb {
                background: rgba(0, 0, 0, 0.2);
                border-radius: 10px;
              }
              
              .chat-textarea::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 0, 0, 0.3);
              }

              .sent-msg-row .sent-hover-toolbar {
                opacity: 0;
                pointer-events: none;
                position: absolute;
                right: 100%;
                top: 50%;
                transform: translateY(-50%);
                margin-right: 6px;
                transition: opacity 0.15s ease;
                z-index: 2;
              }
              .sent-msg-row:hover .sent-hover-toolbar {
                opacity: 1;
                pointer-events: auto;
              }

              .recv-msg-row .recv-hover-toolbar {
                opacity: 0;
                pointer-events: none;
                position: absolute;
                left: 100%;
                top: 50%;
                transform: translateY(-50%);
                margin-left: 6px;
                transition: opacity 0.15s ease;
                z-index: 2;
              }
              .recv-msg-row:hover .recv-hover-toolbar {
                opacity: 1;
                pointer-events: auto;
              }
            `}
          </style>
      <div
        ref={chatBodyRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "16px",
          background: "#fafafa",
          display: "flex",
          flexDirection: "column",
              justifyContent: (messages.length === 0 && !loading) ? "center" : "flex-start",
              alignItems: (messages.length === 0 && !loading) ? "center" : "stretch",
        }}
      >
        {loadingMore && (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <Spin size="small" />
          </div>
        )}
          {messages.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "40px 20px",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: "#F5F6FA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <MessageOutlined
                style={{
                  fontSize: "40px",
                  color: "#9CA3AF",
                }}
              />
            </div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#091647",
                margin: 0,
                marginBottom: "8px",
              }}
            >
                No messages yet
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6B7280",
                margin: 0,
                maxWidth: "300px",
              }}
            >
                Start the conversation by sending a message
            </p>
          </div>
        ) : loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (
          (() => {
            let lastDate = null;
            return messages.map((msg, i) => {
              const messageDate = msg.createdAt ? formatDate(msg.createdAt) : null;
              const showDateSeparator = messageDate && messageDate !== lastDate;
              if (showDateSeparator) {
                lastDate = messageDate;
              }

              const senderIdValue = msg.senderId?._id || msg.senderId?.id || msg.senderId;
              // Check if message is from current user
              let isSent = String(senderIdValue) === String(userId);
              
              // If message was a temp message, it's definitely from current user
              if (msg.isTemp) {
                isSent = true;
              }
              
              // Get profile image and resolved sender info for received messages
              let profileImage = null;
              let resolvedSenderName = null;
              if (!isSent) {
                // Try to resolve from selectedChat.members first (most reliable for group chats)
                const members = selectedChat?.members || [];
                const matchedMember = members.find(
                  (m) => String(m?._id) === String(senderIdValue)
                );

                if (matchedMember) {
                  const rawName =
                    matchedMember?.candidate?.name ||
                    matchedMember?.name ||
                    `${matchedMember?.firstName || ""} ${matchedMember?.lastName || ""}`.trim() ||
                    null;
                  resolvedSenderName = rawName
                    ? rawName.split(" ").filter(Boolean)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                        .join(" ")
                    : null;

                  const rawImg =
                    matchedMember?.candidate?.profileImage ||
                    matchedMember?.profileImage ||
                    null;
                  profileImage = rawImg
                    ? getProfileImageUrl
                      ? getProfileImageUrl(rawImg)
                      : rawImg
                    : null;
                }

                if (!profileImage) {
                  const senderProfileImage =
                    msg.senderId?.profileImage ||
                    msg.senderId?.candidate?.profileImage ||
                    msg.sender?.profileImage ||
                    msg.sender?.candidate?.profileImage;

                  const chatProfileImage = selectedChat?.avatarUrl || selectedChat?.profileImage;

                  if (senderProfileImage) {
                    profileImage = getProfileImageUrl ? getProfileImageUrl(senderProfileImage) : senderProfileImage;
                  } else if (!selectedChat?.isGroup && chatProfileImage) {
                    profileImage = getProfileImageUrl ? getProfileImageUrl(chatProfileImage) : chatProfileImage;
                  }
                }

                if (!resolvedSenderName) {
                  const rawFallback =
                    msg.senderId?.candidate?.name ||
                    msg.senderId?.name ||
                    msg.sender?.candidate?.name ||
                    msg.sender?.name ||
                    `${msg.senderId?.firstName || ""} ${msg.senderId?.lastName || ""}`.trim() ||
                    null;
                  resolvedSenderName = rawFallback
                    ? rawFallback.split(" ").filter(Boolean)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                        .join(" ")
                    : null;
                }
              }

              const messageId = msg._id || msg.id;
              const isHighlighted = highlightedMessageId === messageId;

              const msgText = msg.message || "";
              const isRemovedEvent = msg.memberRemoved || msgText.includes("was removed from the group") || msgText.startsWith("You removed ");
              const isAddedEvent = msg.memberAdded || msgText.includes("was added to the group") || msgText.startsWith("You added ");
              if (isRemovedEvent || isAddedEvent) {
                const lineColor = isRemovedEvent ? "rgba(239,68,68,0.25)" : "rgba(16,185,129,0.25)";
                return (
                  <div
                    key={messageId || i}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "18px 16px",
                      gap: "10px",
                    }}
                  >
                    <div style={{ flex: 1, height: "1px", background: `linear-gradient(to right, transparent, ${lineColor})` }} />
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: isRemovedEvent ? "rgba(239,68,68,0.07)" : "rgba(16,185,129,0.08)",
                        border: `1px solid ${isRemovedEvent ? "rgba(239,68,68,0.25)" : "rgba(16,185,129,0.25)"}`,
                        color: isRemovedEvent ? "#B91C1C" : "#065F46",
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "4px 14px",
                        borderRadius: "20px",
                        whiteSpace: "nowrap",
                        letterSpacing: "0.1px",
                      }}
                    >
                      {msgText}
                    </span>
                    <div style={{ flex: 1, height: "1px", background: `linear-gradient(to left, transparent, ${lineColor})` }} />
                  </div>
                );
              }

              // System notices (archive, campaign removal) — backend text as-is
              if (isSystemGeneratedMessage(msg)) {
                const archiveLabel = getSystemMessageText(msg);
                const lineColor = "rgba(107, 114, 128, 0.35)";
                return (
                  <div key={messageId || i}>
                    {showDateSeparator && (
                      <div
                        style={{
                          textAlign: "center",
                          margin: "16px 0",
                          color: "#777",
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        {messageDate}
                      </div>
                    )}
                    <div
                      ref={(el) => {
                        if (messageId) {
                          messageRefs.current[messageId] = el;
                        }
                      }}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "18px 16px",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: "1px",
                          background: `linear-gradient(to right, transparent, ${lineColor})`,
                        }}
                      />
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          background: "rgba(107, 114, 128, 0.08)",
                          border: "1px solid rgba(107, 114, 128, 0.22)",
                          color: "#4B5563",
                          fontSize: "12px",
                          fontWeight: 500,
                          padding: "6px 14px",
                          borderRadius: "20px",
                          maxWidth: "75%",
                          textAlign: "center",
                          lineHeight: 1.4,
                          letterSpacing: "0.1px",
                          whiteSpace: "normal",
                        }}
                      >
                        <InboxOutlined style={{ fontSize: "13px", color: "#6B7280", flexShrink: 0 }} />
                        <span>{archiveLabel}</span>
                      </span>
                      <div
                        style={{
                          flex: 1,
                          height: "1px",
                          background: `linear-gradient(to left, transparent, ${lineColor})`,
                        }}
                      />
                    </div>
                  </div>
                );
              }

              return (
                <div key={messageId || i}>
                  {showDateSeparator && (
                    <div
                      style={{
                        textAlign: "center",
                        margin: "16px 0",
                        color: "#777",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {messageDate}
                    </div>
                  )}

                  {isSent ? (
                    isChatMessageDeleted(msg) ? (
                      <div
                        ref={(el) => {
                          if (messageId) {
                            messageRefs.current[messageId] = el;
                          }
                        }}
                        style={{
                          textAlign: "right",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            background: "rgba(255, 205, 60, 0.3)",
                            border: "1px solid rgba(255, 205, 60, 0.5)",
                            padding: "8px 12px",
                            borderRadius: "12px",
                            maxWidth: "85%",
                          }}
                        >
                          <DeleteOutlined style={{ fontSize: "14px", color: "#9CA3AF" }} />
                          <span style={{ fontSize: "14px", color: "#6B7280", fontStyle: "italic" }}>
                            {getStatusMessageLabel(msg, { isOwn: true })}
                          </span>
                          <span style={{ fontSize: "11px", color: "#9CA3AF", marginLeft: "4px", whiteSpace: "nowrap" }}>
                            {formatTime(msg.createdAt)}
                          </span>
                        </div>
                      </div>
                    ) : (
                    <div
                      className="sent-msg-row"
                      ref={(el) => {
                        if (messageId) {
                          messageRefs.current[messageId] = el;
                        }
                      }}
                      style={{
                        textAlign: "right",
                        marginBottom: "12px",
                        padding: isHighlighted ? "4px" : "0",
                        borderRadius: isHighlighted ? "8px" : "0",
                        background: isHighlighted ? "rgba(255, 205, 60, 0.3)" : "transparent",
                        animation: isHighlighted ? "pulse 0.5s ease-in-out" : "none",
                      }}
                                    >
                                      <div
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          maxWidth: "85%",
                                          position: "relative",
                                        }}
                                      >
                                        {ENABLE_MESSAGE_ACTIONS && !msg.isTemp && !isStatusStyleMessage(msg) && (
                                          <div
                                            className="sent-hover-toolbar"
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "2px",
                                              background: "#fff",
                                              borderRadius: "8px",
                                              padding: "4px",
                                              boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
                                            }}
                                          >
                                            <div
                                              style={{
                                                padding: "5px",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "background 0.2s ease",
                                              }}
                                              onClick={() => handleReply(msg)}
                                              onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 14L4 9L9 4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M20 20V13C20 11.9391 19.5786 10.9217 18.8284 10.1716C18.0783 9.42143 17.0609 9 16 9H4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                            </div>
                                            {canEditMessage(msg) && (
                                              <div
                                                style={{
                                                  padding: "5px",
                                                  borderRadius: "6px",
                                                  cursor: "pointer",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  transition: "background 0.2s ease",
                                                }}
                                                onClick={() => handleEdit(msg)}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                              >
                                                <EditOutlined style={{ fontSize: "14px", color: "#6B7280" }} />
                                              </div>
                                            )}
                                            <div
                                              style={{
                                                padding: "5px",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "background 0.2s ease",
                                              }}
                                              onClick={() => openDeleteModal(messageId)}
                                              onMouseEnter={(e) => { e.currentTarget.style.background = "#FEE2E2"; }}
                                              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                              <DeleteOutlined style={{ fontSize: "14px", color: "#6B7280" }} />
                                            </div>
                                          </div>
                                        )}
                      <div
                        className="message-box-scroll"
                        style={{
                          display: "inline-block",
                          background: "#ffcd3c",
                          color: "#000",
                          padding: "8px 12px",
                          borderRadius: "12px",
                                          maxWidth: "100%",
                          maxHeight: "300px",
                          textAlign: "left",
                          boxShadow: isHighlighted ? "0 0 0 3px rgba(255, 205, 60, 0.5)" : "none",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {(msg.replyTo || msg.replyToMessage) && (() => {
                          const replyData = msg.replyToMessage || (typeof msg.replyTo === 'object' ? msg.replyTo : findOriginalMessage(msg.replyTo));
                          if (!replyData) return null;
                          const replyType = String(replyData.type || "").toLowerCase();
                          const isReplyDoc = replyType === "document" || replyType === "media";
                          const replySenderId = replyData.senderId?._id || replyData.senderId?.id || replyData.senderId;
                          const isReplySelf = String(replySenderId) === String(userId);
                          const replySenderName = isReplySelf ? "You" : (replyData.senderName || replyData.senderId?.name || selectedChat?.name || "User");
                          return (
                            <div
                              onClick={() => scrollToMessage(replyData._id || replyData.id)}
                              style={{
                                background: "rgba(255,255,255,0.5)",
                                borderLeft: "3px solid #0A1444",
                                borderRadius: "4px",
                                padding: "6px 10px",
                                marginBottom: "6px",
                                cursor: "pointer",
                                maxWidth: "100%",
                              }}
                            >
                              <div style={{ fontSize: "11px", fontWeight: 700, color: "#0A1444", marginBottom: "2px" }}>
                                {replySenderName}
                              </div>
                              <div style={{ fontSize: "12px", color: "#4B5563", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {isStatusStyleMessage(replyData)
                                  ? getStatusMessageLabel(replyData, {
                                      isOwn: isReplySelf,
                                      members: selectedChat?.members || [],
                                    })
                                  : isReplyDoc
                                    ? "📎 Attachment"
                                    : ((replyData.message || "").length > 80 ? (replyData.message || "").substring(0, 80) + "..." : (replyData.message || ""))}
                              </div>
                            </div>
                          );
                        })()}
                        {isDocumentOrMediaMessage(msg) ? (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", maxWidth: "320px" }}>
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #FECE3C",
                                borderTop: "3px solid #FECE3C",
                                borderRadius: "8px",
                                padding: "12px",
                                cursor: "pointer",
                                width: "100%",
                                transition: "all 0.2s ease",
                              }}
                              onClick={() => handleAttachmentClick(msg.message)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                <div style={{ position: "relative" }}>
                                  <FileTextOutlined style={{ fontSize: "32px", color: "#6B7280" }} />
                                  {msg.isTemp && (
                                    <Spin 
                                      size="small" 
                                      style={{ 
                                        position: "absolute",
                                        top: "-4px",
                                        right: "-4px"
                                      }} 
                                    />
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    fontSize: "14px", 
                                    color: "#111827",
                                    marginBottom: "2px"
                                  }}>
                                    {getFileName(msg.message)}
                                  </div>
                                  <div style={{ 
                                    fontSize: "12px", 
                                    color: "#6B7280"
                                  }}>
                                    Open document to view content
                                  </div>
                                </div>
                              </div>
                              <div style={{ 
                                borderTop: "1px solid #E5E7EB", 
                                paddingTop: "8px", 
                                marginTop: "8px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                color: "#111827",
                                fontSize: "12px",
                                fontWeight: 500
                              }}>
                                <span>Open</span>
                                <LinkOutlined style={{ fontSize: "12px" }} />
                              </div>
                            </div>
                            {msg.caption ? (
                              <div style={{ position: "relative", display: "inline-block", width: "100%", marginTop: "6px" }}>
                                <span style={{
                                  fontSize: "14px",
                                  color: "#000",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}>
                                  {msg.caption}
                                </span>
                                <span style={{ 
                                  display: "inline-block", 
                                  width: (msg.isEdited || msg.edited) ? "90px" : "60px",
                                  height: "1px",
                                }} />
                                <span
                                  style={{
                                    position: "absolute",
                                    bottom: "0",
                                    right: "0",
                                    fontSize: "11px",
                                    color: "#777",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {(msg.isEdited || msg.edited) && (
                                    <span style={{ fontStyle: "italic", color: "#9CA3AF" }}>edited</span>
                                  )}
                                  {formatTime(msg.createdAt)}
                                </span>
                              </div>
                            ) : (
                              <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: "4px",
                                marginTop: "4px",
                                width: "100%",
                                fontSize: "11px",
                                color: "#777",
                              }}>
                                {(msg.isEdited || msg.edited) && (
                                  <span style={{ fontStyle: "italic", color: "#9CA3AF" }}>edited</span>
                                )}
                                {formatTime(msg.createdAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                            <span style={{ 
                              wordWrap: "break-word", 
                              overflowWrap: "break-word", 
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap"
                            }}>
                              {msg.message}
                            </span>
                            <span style={{ 
                              display: "inline-block", 
                              width: (msg.isEdited || msg.edited) ? "90px" : "60px",
                              height: "1px",
                            }} />
                            <span
                              style={{
                                position: "absolute",
                                bottom: "0",
                                right: "0",
                                fontSize: "11px",
                                color: "#777",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(msg.isEdited || msg.edited) && (
                                <span style={{ fontStyle: "italic", color: "#9CA3AF" }}>edited</span>
                              )}
                              {formatTime(msg.createdAt)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                                    </div>
                                    )
                                  ) : (
                                    isChatMessageDeleted(msg) ? (
                                      <div
                                        ref={(el) => {
                                          if (messageId) {
                                            messageRefs.current[messageId] = el;
                                          }
                                        }}
                                        style={{
                                          display: "flex",
                                          alignItems: "flex-end",
                                          marginBottom: "12px",
                                          gap: "8px",
                                        }}
                                      >
                                        <Avatar
                                          src={profileImage}
                                          size={32}
                                          style={{ flexShrink: 0, backgroundColor: "#E5E7EB" }}
                                        >
                                          {!profileImage && (() => {
                                            const nameStr = resolvedSenderName || selectedChat?.name || "";
                                            if (!nameStr) return "U";
                                            const nameParts = nameStr.trim().split(" ");
                                            if (nameParts.length >= 2) {
                                              return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
                                            }
                                            return nameStr.charAt(0).toUpperCase();
                                          })()}
                                        </Avatar>
                                        <div>
                                          <div
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "6px",
                                              background: "rgba(0, 0, 0, 0.05)",
                                              border: "1px solid rgba(0, 0, 0, 0.1)",
                                              padding: "8px 12px",
                                              borderRadius: "12px",
                                            }}
                                          >
                                            <DeleteOutlined style={{ fontSize: "14px", color: "#9CA3AF" }} />
                                            <span style={{ fontSize: "14px", color: "#6B7280", fontStyle: "italic" }}>
                                              {getStatusMessageLabel(msg, {
                                                isOwn: false,
                                                senderName:
                                                  selectedChat?.isGroup && resolvedSenderName
                                                    ? resolvedSenderName
                                                    : "",
                                              })}
                                            </span>
                                            <span style={{ fontSize: "11px", color: "#9CA3AF", marginLeft: "4px", whiteSpace: "nowrap" }}>
                                              {formatTime(msg.createdAt)}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                    <div
                      className="recv-msg-row"
                      ref={(el) => {
                        if (messageId) {
                          messageRefs.current[messageId] = el;
                        }
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "12px",
                        gap: "8px",
                        padding: isHighlighted ? "4px" : "0",
                        borderRadius: isHighlighted ? "8px" : "0",
                        background: isHighlighted ? "rgba(255, 205, 60, 0.3)" : "transparent",
                        animation: isHighlighted ? "pulse 0.5s ease-in-out" : "none",
                      }}
                    >
                      <Avatar
                        src={profileImage}
                        size={32}
                        style={{ flexShrink: 0, backgroundColor: "#E5E7EB" }}
                      >
                        {!profileImage && (() => {
                          const nameStr = resolvedSenderName || selectedChat?.name || "";
                          if (!nameStr) return "U";
                          const nameParts = nameStr.trim().split(" ");
                          if (nameParts.length >= 2) {
                            return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
                          }
                          return nameStr.charAt(0).toUpperCase();
                        })()}
                      </Avatar>
                      <div style={{ position: "relative", maxWidth: "85%" }}>
                      <div
                        className="message-box-scroll"
                        style={{
                          background: "#fff8e1",
                          padding: "8px 12px",
                          borderRadius: "12px",
                          maxHeight: "300px",
                          border: "1px solid #eee",
                          boxShadow: isHighlighted ? "0 0 0 3px rgba(255, 205, 60, 0.5)" : "none",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {selectedChat?.isGroup && resolvedSenderName && (
                          <div
                            style={{
                              color: "#683E03",
                              fontFamily: "Arial",
                              fontSize: "0.875rem",
                              fontStyle: "normal",
                              fontWeight: 400,
                              lineHeight: "normal",
                              marginBottom: "4px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            {resolvedSenderName}
                          </div>
                        )}
                        {(msg.replyTo || msg.replyToMessage) && (() => {
                          const replyData = msg.replyToMessage || (typeof msg.replyTo === 'object' ? msg.replyTo : findOriginalMessage(msg.replyTo));
                          if (!replyData) return null;
                          const replyType = String(replyData.type || "").toLowerCase();
                          const isReplyDoc = replyType === "document" || replyType === "media";
                          const replySenderId = replyData.senderId?._id || replyData.senderId?.id || replyData.senderId;
                          const isReplySelf = String(replySenderId) === String(userId);
                          const replySenderName = isReplySelf ? "You" : (replyData.senderName || replyData.senderId?.name || selectedChat?.name || "User");
                          return (
                            <div
                              onClick={() => scrollToMessage(replyData._id || replyData.id)}
                              style={{
                                background: "rgba(255,255,255,0.6)",
                                borderLeft: "3px solid #0A1444",
                                borderRadius: "4px",
                                padding: "6px 10px",
                                marginBottom: "6px",
                                cursor: "pointer",
                                maxWidth: "100%",
                              }}
                            >
                              <div style={{ fontSize: "11px", fontWeight: 700, color: "#0A1444", marginBottom: "2px" }}>
                                {replySenderName}
                              </div>
                              <div style={{ fontSize: "12px", color: "#4B5563", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {isStatusStyleMessage(replyData)
                                  ? getStatusMessageLabel(replyData, {
                                      isOwn: isReplySelf,
                                      members: selectedChat?.members || [],
                                    })
                                  : isReplyDoc
                                    ? "📎 Attachment"
                                    : ((replyData.message || "").length > 80 ? (replyData.message || "").substring(0, 80) + "..." : (replyData.message || ""))}
                              </div>
                            </div>
                          );
                        })()}
                        {isDocumentOrMediaMessage(msg) ? (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", maxWidth: "320px" }}>
                            <div
                              style={{
                                background: "#fff",
                                border: "1px solid #E5E7EB",
                                borderTop: "3px solid #FECE3C",
                                borderRadius: "8px",
                                padding: "12px",
                                cursor: "pointer",
                                width: "100%",
                                transition: "all 0.2s ease",
                              }}
                              onClick={() => handleAttachmentClick(msg.message)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                <FileTextOutlined style={{ fontSize: "32px", color: "#6B7280" }} />
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontWeight: 600, 
                                    fontSize: "14px", 
                                    color: "#111827",
                                    marginBottom: "2px"
                                  }}>
                                    {getFileName(msg.message)}
                                  </div>
                                  <div style={{ 
                                    fontSize: "12px", 
                                    color: "#6B7280"
                                  }}>
                                    Open document to view content
                                  </div>
                                </div>
                              </div>
                              <div style={{ 
                                borderTop: "1px solid #E5E7EB", 
                                paddingTop: "8px", 
                                marginTop: "8px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                color: "#111827",
                                fontSize: "12px",
                                fontWeight: 500
                              }}>
                                <span>Open</span>
                                <LinkOutlined style={{ fontSize: "12px" }} />
                              </div>
                            </div>
                            {msg.caption ? (
                              <div style={{ position: "relative", display: "inline-block", width: "100%", marginTop: "6px" }}>
                                <span style={{
                                  fontSize: "14px",
                                  color: "#333",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                }}>
                                  {msg.caption}
                                </span>
                                <span style={{ 
                                  display: "inline-block", 
                                  width: (msg.isEdited || msg.edited) ? "90px" : "60px",
                                  height: "1px",
                                }} />
                                <span
                                  style={{
                                    position: "absolute",
                                    bottom: "0",
                                    right: "0",
                                    fontSize: "11px",
                                    color: "#777",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {(msg.isEdited || msg.edited) && (
                                    <span style={{ fontStyle: "italic", color: "#9CA3AF" }}>edited</span>
                                  )}
                                  {formatTime(msg.createdAt)}
                                </span>
                              </div>
                            ) : (
                              <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: "4px",
                                marginTop: "4px",
                                width: "100%",
                                fontSize: "11px",
                                color: "#777",
                              }}>
                                {(msg.isEdited || msg.edited) && (
                                  <span style={{ fontStyle: "italic", color: "#9CA3AF" }}>edited</span>
                                )}
                                {formatTime(msg.createdAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
                            <span style={{ 
                              wordWrap: "break-word", 
                              overflowWrap: "break-word", 
                              wordBreak: "break-word",
                              whiteSpace: "pre-wrap"
                            }}>
                              {msg.message}
                            </span>
                            <span style={{ 
                              display: "inline-block", 
                              width: (msg.isEdited || msg.edited) ? "90px" : "60px",
                              height: "1px",
                            }} />
                            <span
                              style={{
                                position: "absolute",
                                bottom: "0",
                                right: "0",
                                fontSize: "11px",
                                color: "#777",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(msg.isEdited || msg.edited) && (
                                <span style={{ fontStyle: "italic", color: "#9CA3AF" }}>edited</span>
                              )}
                              {formatTime(msg.createdAt)}
                            </span>
                          </div>
                        )}
                      </div>
                      {ENABLE_MESSAGE_ACTIONS && !isStatusStyleMessage(msg) && (
                        <div
                          className="recv-hover-toolbar"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                            background: "#fff",
                            borderRadius: "8px",
                            padding: "4px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
                          }}
                        >
                          <div
                            style={{
                              padding: "5px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "background 0.2s ease",
                            }}
                            onClick={() => handleReply(msg)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 14L4 9L9 4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M20 20V13C20 11.9391 19.5786 10.9217 18.8284 10.1716C18.0783 9.42143 17.0609 9 16 9H4" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                    )
                  )}
                </div>
              );
            });
          })()
        )}
      </div>
        </>
      )}

      {/* Members Modal — group chats only; Direct/private chats have no member flow */}
      {ENABLE_GROUP_CHAT && (selectedChat?.isGroup === true || selectedChat?.type === "Group" || selectedChat?.type === "group") && (
      <Modal
        open={membersDrawerVisible && !!selectedChat}
        onCancel={() => setMembersDrawerVisible(false)}
        centered
        getContainer={document.body}
        width={560}
        title={<span style={{ fontSize: "18px", fontWeight: 700, color: "#091647" }}>{selectedChat?.isGroup ? "Edit group members" : "Edit chat members"}</span>}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "4px" }}>
            <Button
              type="primary"
              style={{
                borderRadius: "8px",
                background: "#091647",
                borderColor: "#091647",
                fontWeight: 600,
                fontSize: "14px",
                height: "44px",
                padding: "0 28px",
              }}
              onClick={() => setMembersDrawerVisible(false)}
            >
              Close
            </Button>
          </div>
        }
        destroyOnClose
      >
        {/* Search */}
        <div style={{ marginBottom: "12px" }}>
          <Input
            placeholder="Search members"
            prefix={<SearchOutlined style={{ color: "#1638B1" }} />}
            value={memberSearchText}
            onChange={(e) => setMemberSearchText(e.target.value)}
            allowClear
            style={{ borderRadius: "8px", border: "1.5px solid #D0D5DD", height: "44px" }}
          />
        </div>

        {/* List */}
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {loadingParticipants ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="small" />
            </div>
          ) : (
            (() => {
              const currentMemberIds = new Set(localMembers.map((m) => String(m?._id)));
              const removedMemberIds = new Set(removedMembers.map((m) => String(m?._id)));

              const newParticipants = availableParticipants.filter((p) => {
                const pid = String(p?._id || p?.userId?._id || p?.profileId?.userId?._id || "");
                return pid && pid !== String(userId) && !currentMemberIds.has(pid) && !removedMemberIds.has(pid);
              });

              const currentItems = localMembers.map((m) => ({ type: "current", data: m }));
              const availableItems = newParticipants.map((p) => ({ type: "available", data: p }));
              const removedItems = removedMembers.map((m) => ({ type: "removed", data: m }));

              const searchFilter = (name) => {
                if (!memberSearchText.trim()) return true;
                return name.toLowerCase().includes(memberSearchText.toLowerCase());
              };

              const hasValidName = (name) => {
                const trimmed = (name || "").trim();
                return trimmed.length > 0 && trimmed.toLowerCase() !== "unknown";
              };

              const filteredCurrent = currentItems.filter((item) => {
                const n = item.data?.candidate?.name || item.data?.name ||
                  `${item.data?.firstName || ""} ${item.data?.lastName || ""}`.trim() ||
                  item.data?.email || "";
                if (!hasValidName(n)) return false;
                return searchFilter(n);
              });

              const filteredAvailable = availableItems.filter((item) => {
                const n = item.data?.name || item.data?.profileId?.name || item.data?.candidateName ||
                  `${item.data?.firstName || ""} ${item.data?.lastName || ""}`.trim() || "";
                if (!hasValidName(n)) return false;
                return searchFilter(n);
              });

              const filteredRemoved = removedItems.filter((item) => {
                const n = item.data?.candidate?.name || item.data?.name ||
                  `${item.data?.firstName || ""} ${item.data?.lastName || ""}`.trim() ||
                  item.data?.email || "";
                if (!hasValidName(n)) return false;
                return searchFilter(n);
              });

              const allFiltered = [...filteredCurrent, ...filteredAvailable, ...filteredRemoved];

              if (allFiltered.length === 0) {
                return (
                  <div style={{ textAlign: "center", padding: "40px", color: "#6B7280", fontSize: "14px" }}>
                    No members found
                  </div>
                );
              }

              const renderMemberRow = (item, index) => {
                if (item.type === "current") {
                  const member = item.data;
                  const memberId = String(member?._id);
                  const isCurrentUser = memberId === String(userId);
                  const isCandidate = Boolean(
                    member?.candidate ||
                    member?.candidateName ||
                    member?.profileId?.candidate ||
                    member?.userType === "Associate" ||
                    member?.role === "candidate"
                  );
                  const rawName = member?.candidate?.name || member?.name ||
                    `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || member?.email || "Unknown";
                  const formattedName = rawName.trim() || "Unknown";
                  const rawImg = member?.candidate?.profileImage || member?.profileImage || null;
                  const memberImg = rawImg ? (getProfileImageUrl ? getProfileImageUrl(rawImg) : rawImg) : null;
                  const isLoading = actionLoadingId === memberId;

                  return (
                    <div
                      key={`cur-${memberId || index}`}
                      style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: "1px solid #F5F5F5" }}
                    >
                      <Avatar size={44} src={memberImg} style={{ backgroundColor: "#E5E7EB", flexShrink: 0 }}>
                        {(() => {
                          const p = formattedName.trim().split(" ");
                          return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : formattedName[0]?.toUpperCase();
                        })()}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "15px", color: "#091647", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {formattedName}{isCurrentUser ? " (You)" : ""}
                        </div>
                      </div>
                      {!isCurrentUser && !isCandidate && (
                        <Button
                          size="small"
                          loading={isLoading}
                          disabled={actionLoadingId !== null && !isLoading}
                          onClick={() => handleRemoveMember(memberId)}
                          style={{
                            borderRadius: "6px",
                            border: "1.5px solid #EF4444",
                            color: "#EF4444",
                            background: "#fff",
                            fontWeight: 600,
                            fontSize: "13px",
                            height: "32px",
                            minWidth: 96,
                            padding: "0 12px",
                            flexShrink: 0,
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  );
                }

                if (item.type === "available") {
                  const participant = item.data;
                  const participantId = String(participant?._id || participant?.userId?._id || participant?.profileId?.userId?._id || "");
                  const rawName = participant?.name || participant?.profileId?.name || participant?.candidateName ||
                    `${participant?.firstName || ""} ${participant?.lastName || ""}`.trim() || "Unknown";
                  const formattedName = rawName.trim() || "Unknown";
                  const rawImg = participant?.profileImage || participant?.profileId?.profileImage || null;
                  const partImg = rawImg ? (getProfileImageUrl ? getProfileImageUrl(rawImg) : rawImg) : null;
                  const permissionLabel = participant?.permission || participant?.role || "";
                  const isLoading = actionLoadingId === participantId;

                  return (
                    <div
                      key={`avail-${participantId || index}`}
                      style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: "1px solid #F5F5F5" }}
                    >
                      <Avatar size={44} src={partImg} style={{ backgroundColor: "#E5E7EB", flexShrink: 0 }}>
                        {(() => {
                          const p = formattedName.trim().split(" ");
                          return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : formattedName[0]?.toUpperCase();
                        })()}
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: "15px", color: "#091647", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {formattedName}
                        </div>
                        {permissionLabel && (
                          <div style={{ fontSize: "12px", color: "#6B7280", textTransform: "capitalize" }}>{permissionLabel}</div>
                        )}
                      </div>
                      <Button
                        size="small"
                        loading={isLoading}
                        disabled={actionLoadingId !== null && !isLoading}
                        onClick={() => handleAddMember(participant)}
                        style={{
                          borderRadius: "6px",
                          border: "1.5px solid #1638B1",
                          color: "#1638B1",
                          background: "#fff",
                          fontWeight: 600,
                          fontSize: "13px",
                          height: "32px",
                          minWidth: 96,
                          padding: "0 12px",
                          flexShrink: 0,
                        }}
                      >
                        + Add
                      </Button>
                    </div>
                  );
                }

                const member = item.data;
                const memberId = String(member?._id);
                const rawName = member?.candidate?.name || member?.name ||
                  `${member?.firstName || ""} ${member?.lastName || ""}`.trim() || member?.email || "Unknown";
                const formattedName = rawName.trim() || "Unknown";
                const rawImg = member?.candidate?.profileImage || member?.profileImage || null;
                const memberImg = rawImg ? (getProfileImageUrl ? getProfileImageUrl(rawImg) : rawImg) : null;
                const isLoading = actionLoadingId === memberId;

                return (
                  <div
                    key={`removed-${memberId || index}`}
                    style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: "1px solid #F5F5F5", opacity: 0.75 }}
                  >
                    <Avatar size={44} src={memberImg} style={{ backgroundColor: "#E5E7EB", flexShrink: 0 }}>
                      {(() => {
                        const p = formattedName.trim().split(" ");
                        return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : formattedName[0]?.toUpperCase();
                      })()}
                    </Avatar>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: "15px", color: "#091647", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formattedName}
                      </div>
                    </div>
                    <Button
                      size="small"
                      loading={isLoading}
                      disabled={actionLoadingId !== null && !isLoading}
                      onClick={() => handleReAddMember(member)}
                      style={{
                        borderRadius: "6px",
                        border: "1.5px solid #1638B1",
                        color: "#1638B1",
                        background: "#fff",
                        fontWeight: 600,
                        fontSize: "13px",
                        height: "32px",
                        minWidth: 96,
                        padding: "0 12px",
                        flexShrink: 0,
                      }}
                    >
                      + Add
                    </Button>
                  </div>
                );
              };

              return (
                <>
                  {filteredCurrent.map((item, i) => renderMemberRow(item, i))}
                  {filteredAvailable.map((item, i) => renderMemberRow(item, `a${i}`))}
                  {filteredRemoved.length > 0 && (
                    <>
                      {/* <div style={{ padding: "10px 0 4px", fontSize: "11px", fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                        Removed
                      </div> */}
                      {filteredRemoved.map((item, i) => renderMemberRow(item, `r${i}`))}
                    </>
                  )}
                </>
              );
            })()
          )}
        </div>
      </Modal>
      )}

      {/* Media Drawer */}
      {mediaModalVisible && (
        <>
          {/* Click-away overlay */}
          <div
            onClick={() => setMediaModalVisible(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "transparent",
              zIndex: 900,
            }}
          />

          {/* Drawer */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "400px",
              height: "100%",
              background: "#fff",
              borderLeft: "1px solid #eee",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Drawer Header */}
            <div
              style={{
                padding: "16px",
                background: "#FFF",
                borderBottom: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#091647",
                }}
              >
                Media
              </h3>
              <CloseOutlined
                style={{
                  fontSize: "16px",
                  cursor: "pointer",
                  color: "#666",
                }}
                onClick={() => setMediaModalVisible(false)}
              />
            </div>

            {/* Drawer Content */}
            <div style={{ padding: "16px", flex: 1, overflowY: "auto" }}>
              {loadingMedia ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="small" />
                </div>
              ) : mediaMessages.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6B7280",
                    fontSize: "14px",
                  }}
                >
                  No media files shared in this chat
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {mediaMessages.map((msg, index) => {
                    const msgType = String(msg.type || "").toLowerCase();
                    const mediaUrl = msgType === "document" 
                      ? getAttachmentUrl(msg.message)
                      : getMediaUrl(msg.message);
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(msg.message || "");
                    
                    return (
                      <div
                        key={msg._id || msg.id || index}
                        style={{
                          position: "relative",
                          cursor: "pointer",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "1px solid #E5E7EB",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => {
                          if (mediaUrl) {
                            window.open(mediaUrl, '_blank');
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
                          e.currentTarget.style.transform = "scale(1.02)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        {isImage && mediaUrl ? (
                          <Image
                            src={mediaUrl}
                            alt="Media"
                            style={{
                              width: "100%",
                              height: "100px",
                              objectFit: "cover",
                            }}
                            preview={false}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100px",
                              background: "#F3F4F6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <FileTextOutlined style={{ fontSize: "32px", color: "#9CA3AF" }} />
                          </div>
                        )}
                        <div
                          style={{
                            padding: "4px",
                            background: "#fff",
                            borderTop: "1px solid #E5E7EB",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#6B7280",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                            }}
                          >
                            {formatTime(msg.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Custom Search Drawer */}
      {openDrawer && (
        <>
          {/* Click-away overlay */}
          <div
            onClick={() => setOpenDrawer(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "transparent",
              zIndex: 900,
            }}
          />

          {/* Drawer */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "400px",
              height: "100%",
              background: "#fff",
              borderLeft: "1px solid #eee",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              boxShadow: "-2px 0 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Drawer Header */}
            <div
              style={{
                padding: "16px",
                background: "#FFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                Search Chat
              </h3>
              <Tooltip title="Close" placement="bottom">
                <span
                  onClick={() => setOpenDrawer(false)}
                  style={{
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    color: "#242424",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#F3F4F6"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <CloseOutlined style={{ fontSize: "16px" }} />
                </span>
              </Tooltip>
            </div>

            {/* Drawer Content */}
            <div style={{ padding: "16px", flex: 1, overflowY: "auto" }}>
              <Input
                ref={searchInputRef}
                placeholder="Search..."
                style={{ marginBottom: 16 }}
                allowClear={true}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                prefix={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 20 21"
                    fill="none"
                  >
                    <path
                      d="M19.0002 19.5002L14.6572 15.1572M14.6572 15.1572C15.4001 14.4143 15.9894 13.5324 16.3914 12.5618C16.7935 11.5911 17.0004 10.5508 17.0004 9.50021C17.0004 8.4496 16.7935 7.40929 16.3914 6.43866C15.9894 5.46803 15.4001 4.58609 14.6572 3.84321C13.9143 3.10032 13.0324 2.51103 12.0618 2.10898C11.0911 1.70693 10.0508 1.5 9.00021 1.5C7.9496 1.5 6.90929 1.70693 5.93866 2.10898C4.96803 2.51103 4.08609 3.10032 3.34321 3.84321C1.84288 5.34354 1 7.37842 1 9.50021C1 11.622 1.84288 13.6569 3.34321 15.1572C4.84354 16.6575 6.87842 17.5004 9.00021 17.5004C11.122 17.5004 13.1569 16.6575 14.6572 15.1572Z"
                      stroke="#091647"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
              {loadingSearch ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="small" />
                </div>
              ) : searchMessages.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#6B7280", fontSize: "14px" }}>
                  {searchQuery ? "No messages found" : "No messages yet"}
                </div>
              ) : (
                (() => {
                  const sentMessages = [];
                  const receivedMessages = [];
                  
                  searchMessages.forEach((msg) => {
                    const senderIdValue = msg.senderId?._id || msg.senderId?.id || msg.senderId;
                    const isSent = String(senderIdValue) === String(userId);
                    
                    if (isSent) {
                      sentMessages.push(msg);
                    } else {
                      receivedMessages.push(msg);
                    }
                  });
                  
                  return (
                    <>
                      {receivedMessages.length > 0 && (
              <div>
                <div style={{ padding: "8px", marginBottom: "8px" }}>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#A7AEBB",
                    }}
                  >
                    RECEIVED
                  </p>
                </div>
                          {receivedMessages.map((msg, index) => (
                            <div
                              key={msg._id || msg.id || index}
                              style={{
                                marginBottom: "8px",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              {isStatusStyleMessage(msg) ? (
                                <div
                                  className="message-box-scroll"
                                  style={{
                                    border: "1px solid #FFFBE5",
                                    borderRadius: "12px",
                                    background: "#FFFBE5",
                                    width: "fit-content",
                                    maxWidth: "250px",
                                    padding: "10px 10px 3px 10px",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg._id || msg.id);
                                  }}
                                >
                                  <p style={{
                                    margin: 0,
                                    fontStyle: "italic",
                                    color: "#6B7280",
                                  }}>
                                    {getStatusMessageLabel(msg, {
                                      isOwn: false,
                                      members: selectedChat?.members || [],
                                    })}
                                  </p>
                                  <span
                                    style={{
                                      color: "#909090",
                                      fontSize: "12px",
                                      display: "flex",
                                      justifyContent: "end",
                                      padding: "5px 0 0 0",
                                    }}
                                  >
                                    {formatTime(msg.createdAt)}
                                  </span>
                                </div>
                              ) : isDocumentOrMediaMessage(msg) ? (
                                <div
                                  style={{
                                    background: "#fff",
                                    border: "1px solid #E5E7EB",
                                    borderTop: "3px solid #FECE3C",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    width: "fit-content",
                                    maxWidth: "200px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg._id || msg.id);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "none";
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                    <FileTextOutlined style={{ fontSize: "32px", color: "#6B7280" }} />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ 
                                        fontWeight: 600, 
                                        fontSize: "14px", 
                                        color: "#111827",
                                        marginBottom: "2px"
                                      }}>
                                        {getFileName(msg.message)}
                </div>
                                      <div style={{ 
                                        fontSize: "12px", 
                                        color: "#6B7280"
                                      }}>
                                        Open document to view content
              </div>
                                    </div>
                                  </div>
                                  <div style={{ 
                                    borderTop: "1px solid #E5E7EB", 
                                    paddingTop: "8px", 
                                    marginTop: "8px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}>
                                    <div style={{ 
                                      display: "flex", 
                                      alignItems: "center", 
                                      gap: "4px",
                                      color: "#111827",
                                      fontSize: "12px",
                                      fontWeight: 500
                                    }}>
                                      <span>Open</span>
                                      <LinkOutlined style={{ fontSize: "12px" }} />
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#9CA3AF" }}>
                                      {formatTime(msg.createdAt)}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="message-box-scroll"
                                  style={{
                                    border: "1px solid #FFFBE5",
                                    borderRadius: "12px",
                                    background: "#FFFBE5",
                                    width: "fit-content",
                                    maxWidth: "250px",
                                    maxHeight: "200px",
                                    padding: "10px 10px 3px 10px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    wordBreak: "break-word",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg._id || msg.id);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "none";
                                  }}
                                >
                                  <p style={{ 
                                    margin: 0, 
                                    wordBreak: "break-word",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    whiteSpace: "pre-wrap"
                                  }}>
                                    {msg.message}
                                  </p>
                                  <span
                                    style={{
                                      color: "#909090",
                                      fontSize: "12px",
                                      display: "flex",
                                      justifyContent: "end",
                                      padding: "5px 0 0 0",
                                    }}
                                  >
                                    {formatTime(msg.createdAt)}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {sentMessages.length > 0 && (
              <div>
                          <div style={{ padding: "8px", marginBottom: "8px", marginTop: receivedMessages.length > 0 ? "12px" : "0" }}>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#A7AEBB",
                    }}
                  >
                    SENT
                  </p>
                </div>
                          {sentMessages.map((msg, index) => (
                            <div
                              key={msg._id || msg.id || index}
                              style={{
                                marginLeft: "20px",
                                marginBottom: "8px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                              }}
                            >
                              {isStatusStyleMessage(msg) ? (
                                <div
                                  className="message-box-scroll"
                                  style={{
                                    border: "1px solid #FECE3C",
                                    borderRadius: "12px",
                                    background: "#FECE3C",
                                    width: "fit-content",
                                    maxWidth: "250px",
                                    padding: "10px 10px 3px 10px",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg._id || msg.id);
                                  }}
                                >
                                  <p style={{
                                    margin: 0,
                                    fontStyle: "italic",
                                    color: "#6B7280",
                                  }}>
                                    {getStatusMessageLabel(msg, {
                                      isOwn: true,
                                      members: selectedChat?.members || [],
                                    })}
                                  </p>
                                  <span
                                    style={{
                                      color: "#909090",
                                      fontSize: "12px",
                                      display: "flex",
                                      justifyContent: "end",
                                      padding: "5px 0 0 0",
                                    }}
                                  >
                                    {formatTime(msg.createdAt)}
                                  </span>
                                </div>
                              ) : isDocumentOrMediaMessage(msg) ? (
                                <div
                                  style={{
                                    background: "#fff",
                                    border: "1px solid #FECE3C",
                                    borderTop: "3px solid #FECE3C",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    width: "fit-content",
                                    maxWidth: "200px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg._id || msg.id);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "none";
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                                    <FileTextOutlined style={{ fontSize: "32px", color: "#6B7280" }} />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ 
                                        fontWeight: 600, 
                                        fontSize: "14px", 
                                        color: "#111827",
                                        marginBottom: "2px"
                                      }}>
                                        {getFileName(msg.message)}
                </div>
                                      <div style={{ 
                                        fontSize: "12px", 
                                        color: "#6B7280"
                                      }}>
                                        Open document to view content
              </div>
            </div>
          </div>
                                  <div style={{ 
                                    borderTop: "1px solid #E5E7EB", 
                                    paddingTop: "8px", 
                                    marginTop: "8px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}>
                                    <div style={{ 
                                      display: "flex", 
                                      alignItems: "center", 
                                      gap: "4px",
                                      color: "#111827",
                                      fontSize: "12px",
                                      fontWeight: 500
                                    }}>
                                      <span>Open</span>
                                      <LinkOutlined style={{ fontSize: "12px" }} />
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#9CA3AF" }}>
                                      {formatTime(msg.createdAt)}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="message-box-scroll"
                                  style={{
                                    border: "1px solid #FECE3C",
                                    borderRadius: "12px",
                                    background: "#FECE3C",
                                    width: "fit-content",
                                    maxWidth: "250px",
                                    maxHeight: "200px",
                                    padding: "10px 10px 3px 10px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    wordBreak: "break-word",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg._id || msg.id);
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "none";
                                  }}
                                >
                                  <p style={{ 
                                    margin: 0, 
                                    wordBreak: "break-word",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    whiteSpace: "pre-wrap"
                                  }}>
                                    {msg.message}
                                  </p>
                                  <span
                                    style={{
                                      color: "#909090",
                                      fontSize: "12px",
                                      display: "flex",
                                      justifyContent: "end",
                                      padding: "5px 0 0 0",
                                    }}
                                  >
                                    {formatTime(msg.createdAt)}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </>
      )}
      {/* Chat Input */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          borderTop: "1px solid #eee",
          background: "#F8F7F3",
          borderRadius: "0 0 12px 12px",
        }}
      >
        {/* Reply Preview */}
        {ENABLE_MESSAGE_ACTIONS && replyToMessage && !editingMessage && (
          <div
            style={{
              padding: "10px 16px",
              background: "#EDE9FE",
              borderBottom: "1px solid #DDD6FE",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <div
              style={{
                flex: 1,
                borderLeft: "3px solid #7C3AED",
                padding: "4px 12px",
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#6D28D9", marginBottom: "3px", display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ fontSize: "13px" }}>←</span> Replying to {replyToMessage.senderName?.toLowerCase() === "you" ? "yourself" : replyToMessage.senderName}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#4B5563",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getReplyPreviewText(replyToMessage)}
              </div>
            </div>
            <CloseOutlined
              style={{
                fontSize: "13px",
                color: "#7C3AED",
                cursor: "pointer",
                padding: "2px",
                flexShrink: 0,
                marginTop: "2px",
              }}
              onClick={handleCancelReply}
            />
          </div>
        )}
        {/* Edit Preview */}
        {ENABLE_MESSAGE_ACTIONS && editingMessage && (
          <div
            style={{
              padding: "10px 16px",
              background: "#FEF3C7",
              borderBottom: "1px solid #FDE68A",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <div
              style={{
                flex: 1,
                borderLeft: "3px solid #D97706",
                padding: "4px 12px",
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#B45309", marginBottom: "3px", display: "flex", alignItems: "center", gap: "4px" }}>
                <EditOutlined style={{ fontSize: "12px" }} /> Editing message
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: "#4B5563",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {editingMessage.message?.length > 80 ? editingMessage.message.substring(0, 80) + "..." : editingMessage.message}
              </div>
            </div>
            <CloseOutlined
              style={{
                fontSize: "13px",
                color: "#B45309",
                cursor: "pointer",
                padding: "2px",
                flexShrink: 0,
                marginTop: "2px",
              }}
              onClick={handleCancelEdit}
            />
          </div>
        )}
        {/* Attachment Preview */}
        {pendingAttachment && (
          <div
            style={{
              padding: "8px 12px",
              background: "#fff",
              borderBottom: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flex: 1,
                padding: "8px 12px",
                background: "#F8FAFC",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
              }}
            >
              <FileTextOutlined style={{ fontSize: "20px", color: "#1638B1" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#1E293B",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pendingAttachment.name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#64748B",
                    marginTop: "2px",
                  }}
                >
                  {(pendingAttachment.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <CloseOutlined
                style={{
                  fontSize: "14px",
                  color: "#64748B",
                  cursor: "pointer",
                  padding: "4px",
                }}
                onClick={handleRemoveAttachment}
              />
            </div>
          </div>
        )}

        {/* Input Row */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            padding: "8px 12px",
        }}
      >
        <TextArea
          ref={textAreaRef}
          className="chat-textarea"
          placeholder={pendingAttachment ? "Add a message (optional)..." : "Type your message..."}
          bordered={false}
          autoSize={{ minRows: 1, maxRows: 6 }}
          style={{ 
            flex: 1, 
            fontSize: "14px",
            maxHeight: "150px",
            overflowY: "auto",
            resize: "none"
          }}
          value={messageInput}
          onChange={(e) => {
            setMessageInput(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessageHandler();
            }
          }}
          disabled={!selectedChat}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          multiple={false}
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
                handleFileSelect(file);
            }
            e.target.value = "";
          }}
            disabled={uploadingFile}
        />
        <PaperClipOutlined
            style={{ 
              fontSize: "18px", 
              marginRight: "12px", 
              cursor: (uploadingFile || !selectedChat) ? "not-allowed" : "pointer",
              opacity: (uploadingFile || !selectedChat) ? 0.5 : 1,
              color: pendingAttachment ? "#1638B1" : "#64748B"
            }}
          onClick={() => {
              if (!uploadingFile && selectedChat) {
            fileInputRef.current?.click();
              }
          }}
        />
          {uploadingFile && (
            <Spin size="small" style={{ marginRight: "12px" }} />
          )}
        <Button 
          onClick={sendMessageHandler}
          type="text"
            disabled={uploadingFile || savingEdit || !selectedChat || (!messageInput.trim() && !pendingAttachment)}
          style={{
            padding: 0,
            border: "none",
            background: "transparent",
            boxShadow: "none",
            minWidth: "unset",
            height: "auto",
              opacity: (uploadingFile || savingEdit || !selectedChat || (!messageInput.trim() && !pendingAttachment)) ? 0.5 : 1,
          }}
        >
          {savingEdit ? (
            <Spin size="small" />
          ) : (
          <img
            src={SendIcon}
            height={30}
            width={30}
            alt="send-icon"
              style={{ cursor: (uploadingFile || savingEdit || !selectedChat || (!messageInput.trim() && !pendingAttachment)) ? "not-allowed" : "pointer" }}
          />
          )}
        </Button>
      </div>
      </div>

      {/* Delete Message Confirmation Modal */}
      {ENABLE_MESSAGE_ACTIONS && (
      <Modal
        className="candidateDeleteModal"
        centered
        width={392}
        zIndex={11000}
        getContainer={document.body}
        onCancel={() => {
          if (!deletingMessageId) {
            setDeleteModalVisible(false);
            setMessageToDelete(null);
          }
        }}
        open={deleteModalVisible}
        footer={null}
        title="Delete Message?"
      >
        <Row className="header">
          <Col>
            <p className="desc">
              Deleting this message will remove it for everyone in this chat. This action is permanent and cannot be reversed.
            </p>
          </Col>
        </Row>

        <Row align="middle" gutter={16} justify="end">
          <Col>
            <Button
              className="confirmBtn"
              onClick={handleDeleteMessage}
              loading={deletingMessageId}
            >
              Delete
            </Button>
          </Col>
          <Col>
            <Button
              className="cancelBtn"
              onClick={() => {
                setDeleteModalVisible(false);
                setMessageToDelete(null);
              }}
              disabled={deletingMessageId}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal>
      )}

    </div>
  );
};

export default ChatScreen;
